// POS Interface Component - Responsive and Dynamic
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Input, Button, Space, Typography, Divider, Row, Col, Tag, Switch, Modal, Form, InputNumber, message, Badge, DatePicker, Select, Popover, Tooltip, Spin } from 'antd';
import './POSInterface.css';
import {
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  SearchOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  HistoryOutlined,
  SaveOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {
  POSCartItem,
  POSProduct,
  POSPaymentMethod,
  POSStashedTransaction,
  POSTransaction,
  POSPaymentSplitLine,
} from '../types/pos';
import { RBACService } from '../utils/rbac';
import {
  getPOSProducts,
  getProductByBarcode,
  stashTransaction,
  getStashedTransactions,
  deleteStashedTransaction,
  completePOSTransaction,
  getTransactionHistory,
  getTransactionById,
  getRefundHistory,
  refundTransaction,
  saveSplitPaymentCache,
  getPOSTaxSettings,
  type POSTaxSettings,
} from '../services/posService';
import type { RefundHistoryItem } from '../services/posService';
import masterDataService, { Category } from '../services/masterDataService';
import { PosIcon, PosIconBadge } from './pos/PosIcon';
import { CashPayIcon, ManualCardPayIcon, MobilePayIcon } from './pos/PosPaymentMethodIcons';
import { CreditBookModal } from './pos/CreditBookModal';
import {
  SplitPaymentEditor,
  createSplitPaymentLine,
  validateSplitPaymentLines,
  paymentMethodLabel,
} from './pos/SplitPaymentEditor';
import MobileTransferPayPanel, { validateMobilePayFields, validatePayerPhone, countPhoneDigits } from './pos/MobileTransferPayPanel';
import { ReceiptPaymentMethods } from './pos/ReceiptPaymentMethods';
import { ReceiptPrintModal } from './pos/ReceiptPrintModal';
import { printPosReceipt, registerReceiptPrintHandler } from '../utils/printPosReceipt';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { DEFAULT_GST_RATE, computeLineGst, gstCartLineCaption, normalizeGstCategory } from '../utils/gst';
import {
  availableSellUnits,
  isGstDemoLabel,
  listPriceFor,
  PriceMode,
  SellUnit,
  sellUnitLabel,
  toBaseQuantity,
  unitsPerSellUnit,
} from '../utils/posPricing';
import { getCartLineTint, posItemTintStyle } from '../utils/posItemTint';
import { tryRefreshOfflineCatalog, getCatalogMeta } from '../offline/posOfflineCache';
import { syncOfflineSalesQueue } from '../offline/posOfflineSync';
import {
  adjustCachedStock,
  createOfflineLocalId,
  createOfflineStash,
  deleteOfflineStash,
  enqueueOfflineSale,
  findCachedProductByBarcode,
  listOfflineStashes,
  offlineSaleToReceiptTransaction,
  saveOfflineStash,
  searchCachedProducts,
} from '../offline/posOfflineStore';

const { Text } = Typography;

type POSView = 'cart' | 'stashed' | 'transactions';

interface POSInterfaceProps {
  onTransactionComplete?: (transaction: POSTransaction) => void;
  currentView?: POSView;
  onViewChange?: (view: POSView) => void;
  onStashedCountUpdate?: (count: number) => void;
}

function formatPOSTransactionLocation(t: POSTransaction): string {
  const name = t.location_name?.trim();
  if (name) return name;
  if (t.location_id != null && Number(t.location_id) > 0) return `Location #${t.location_id}`;
  return '—';
}

const POSInterface: React.FC<POSInterfaceProps> = ({ 
  onTransactionComplete,
  currentView: externalView,
  onViewChange,
  onStashedCountUpdate
}) => {
  const [internalView, setInternalView] = useState<POSView>('cart');
  const currentView = externalView !== undefined ? externalView : internalView;
  const setCurrentView = onViewChange || setInternalView;
  const [cart, setCart] = useState<POSCartItem[]>([]);
  const [products, setProducts] = useState<POSProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<number | 'all'>('all');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [amountTenderedStr, setAmountTenderedStr] = useState('');
  /** Tax rate % applied to taxable subtotal (after line discounts); API still receives computed tax in Nu. */
  const [taxPercent, setTaxPercent] = useState(0);
  const [taxSettings, setTaxSettings] = useState<POSTaxSettings | null>(null);
  const [buyerName, setBuyerName] = useState('');
  const [buyerCid, setBuyerCid] = useState('');
  const [buyerTpn, setBuyerTpn] = useState('');
  const [buyerAddress, setBuyerAddress] = useState('');
  const [orderDisplayId, setOrderDisplayId] = useState(() => Math.floor(1000 + Math.random() * 8999));
  const [paymentMethod, setPaymentMethod] = useState<POSPaymentMethod>(POSPaymentMethod.CASH);
  const [creditCustomerName, setCreditCustomerName] = useState('');
  const [creditBookOpen, setCreditBookOpen] = useState(false);
  const [priceMode, setPriceMode] = useState<PriceMode>('retail');
  const [defaultSellUnit, setDefaultSellUnit] = useState<SellUnit>('base');
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitLines, setSplitLines] = useState<POSPaymentSplitLine[]>([]);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentPayerPhone, setPaymentPayerPhone] = useState('');
  const [stashedList, setStashedList] = useState<POSStashedTransaction[]>([]);
  const [transactionList, setTransactionList] = useState<POSTransaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<POSTransaction | null>(null);
  const [selectedStashed, setSelectedStashed] = useState<POSStashedTransaction | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundItems, setRefundItems] = useState<Array<{ product_id: number; quantity: number }>>([]);
  const [refundHistory, setRefundHistory] = useState<RefundHistoryItem[]>([]);
  const [refundHistoryLoading, setRefundHistoryLoading] = useState(false);
  const [txnSearchInput, setTxnSearchInput] = useState('');
  const [txnSearch, setTxnSearch] = useState('');
  const [txnPayment, setTxnPayment] = useState<string>('ALL');
  const [txnDateRange, setTxnDateRange] = useState<any>(null);
  const [transactionListFilter, setTransactionListFilter] = useState<'all' | 'refunded'>('all');
  const [barcodeScanModalOpen, setBarcodeScanModalOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(() => {
    try {
      const saved = localStorage.getItem('pos_print_receipt');
      if (saved === '0') return false;
      if (saved === '1') return true;
    } catch {
      /* ignore */
    }
    return true;
  });
  const printIntentRef = useRef(printReceipt);
  const handlePrintReceiptChange = (enabled: boolean) => {
    printIntentRef.current = enabled;
    setPrintReceipt(enabled);
    try {
      localStorage.setItem('pos_print_receipt', enabled ? '1' : '0');
    } catch {
      /* ignore */
    }
  };
  const [receiptPrintOpen, setReceiptPrintOpen] = useState(false);
  const [receiptPrintTxn, setReceiptPrintTxn] = useState<POSTransaction | null>(null);
  const [receiptPrintStore, setReceiptPrintStore] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cartScrollRef = useRef<HTMLDivElement | null>(null);
  const cartWheelCleanupRef = useRef<(() => void) | null>(null);
  const checkoutScrollRef = useRef<HTMLDivElement | null>(null);
  const checkoutWheelCleanupRef = useRef<(() => void) | null>(null);
  const { isOnline, pendingOfflineCount, refreshPendingCount } = useNetworkStatus();
  const [offlineSyncing, setOfflineSyncing] = useState(false);
  const [catalogSyncedAt, setCatalogSyncedAt] = useState<string | null>(null);

  // Browse in-stock catalog by default; search/category narrow the list.
  const isSearchNarrowed =
    searchQuery.trim().length >= 2 || selectedParentCategoryId !== 'all';

  // Hide "GST Demo" seed category; promote its children (e.g. food) as top-level pills.
  const posParentCategories = useMemo(() => {
    const roots = categories.filter((c) => !c.parent_id);
    const demoRoots = roots.filter((c) => isGstDemoLabel(c.name));
    const demoIds = new Set(demoRoots.map((c) => c.id));
    const realRoots = roots.filter((c) => !demoIds.has(c.id));
    const promoted = categories.filter(
      (c) => c.parent_id != null && demoIds.has(Number(c.parent_id)) && !isGstDemoLabel(c.name),
    );
    return [...realRoots, ...promoted] as typeof categories;
  }, [categories]);

  const getUserLocationId = () => {
    const u: any = RBACService.getCurrentUser();
    const raw = u?.locationId ?? u?.location_id;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  useEffect(() => {
    const loc = getUserLocationId() ?? undefined;
    getPOSTaxSettings(loc)
      .then(setTaxSettings)
      .catch(() => setTaxSettings(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parent layout (main-content-inner overflow:auto) steals mouse-wheel / trackpad.
  // Bind wheel handlers via callback refs so scrolling works as soon as the node mounts.
  const bindPanelWheelScroll = (
    el: HTMLDivElement | null,
    storeRef: React.MutableRefObject<HTMLDivElement | null>,
    cleanupRef: React.MutableRefObject<(() => void) | null>,
  ) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    storeRef.current = el;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (el.scrollHeight <= el.clientHeight + 1) return;
      event.preventDefault();
      event.stopPropagation();
      el.scrollTop += event.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    cleanupRef.current = () => el.removeEventListener('wheel', onWheel);
  };

  const setCartScrollNode = (el: HTMLDivElement | null) => {
    bindPanelWheelScroll(el, cartScrollRef, cartWheelCleanupRef);
  };

  const setCheckoutScrollNode = (el: HTMLDivElement | null) => {
    bindPanelWheelScroll(el, checkoutScrollRef, checkoutWheelCleanupRef);
  };

  useEffect(() => {
    return () => {
      cartWheelCleanupRef.current?.();
      checkoutWheelCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    return registerReceiptPrintHandler(({ transaction, storeName }) => {
      setReceiptPrintTxn(transaction);
      setReceiptPrintStore(storeName);
      setReceiptPrintOpen(true);
    });
  }, []);

  const syncOfflineSales = React.useCallback(async () => {
    if (!navigator.onLine) return;
    setOfflineSyncing(true);
    try {
      const result = await syncOfflineSalesQueue();
      await refreshPendingCount();
      if (result.synced > 0) {
        message.success(`Synced ${result.synced} offline sale(s) to server`);
        if (currentView === 'transactions') {
          void loadTransactionHistory();
        }
      }
      if (result.failed > 0) {
        message.warning(`${result.failed} offline sale(s) could not sync — check with manager`);
      }
    } catch (err) {
      console.warn('[offline] sync skipped', err);
    } finally {
      setOfflineSyncing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, refreshPendingCount]);

  useEffect(() => {
    const loc = getUserLocationId();
    if (!loc) return;
    if (navigator.onLine) {
      void tryRefreshOfflineCatalog(loc).then(async () => {
        const meta = await getCatalogMeta(loc);
        setCatalogSyncedAt(meta?.synced_at ?? null);
      });
    } else {
      void getCatalogMeta(loc).then((meta) => setCatalogSyncedAt(meta?.synced_at ?? null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOnline) return;
    const loc = getUserLocationId();
    if (loc) {
      void tryRefreshOfflineCatalog(loc).then(async () => {
        const meta = await getCatalogMeta(loc);
        setCatalogSyncedAt(meta?.synced_at ?? null);
      });
    }
    void syncOfflineSales();
  }, [isOnline, syncOfflineSales]);

  // Load initial data (products load only on search / scan / category — not on mount)
  useEffect(() => {
    loadCategories();
    loadStashedTransactions();
    if (isOnline) {
      loadTransactionHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Load data when view changes
  useEffect(() => {
    if (currentView === 'stashed') {
      loadStashedTransactions();
    } else if (currentView === 'transactions') {
      loadTransactionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  // Re-load transactions when filters change (only while on transactions tab)
  useEffect(() => {
    if (currentView === 'transactions') {
      loadTransactionHistory();
      setSelectedTransaction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txnSearch, txnPayment, txnDateRange]);

  // Load refund history when refund modal opens for a transaction
  useEffect(() => {
    if (showRefundModal && selectedTransaction?.id) {
      setRefundHistoryLoading(true);
      getRefundHistory(selectedTransaction.id)
        .then((history) => {
          setRefundHistory(history);
          const refundedByProduct = new Map<number, number>();
          const refundedByName = new Map<string, number>();
          for (const row of history) {
            for (const line of row.items_summary || []) {
              const qty = Number(line.quantity) || 0;
              if (line.product_id != null) {
                refundedByProduct.set(line.product_id, (refundedByProduct.get(line.product_id) || 0) + qty);
              }
              if (line.product_name) {
                refundedByName.set(line.product_name, (refundedByName.get(line.product_name) || 0) + qty);
              }
            }
          }
          setRefundItems(
            selectedTransaction.items
              .map((item) => {
                const already =
                  refundedByProduct.get(item.product_id) ||
                  refundedByName.get(item.product_name) ||
                  0;
                const left = Math.max(0, item.quantity - already);
                return { product_id: item.product_id, quantity: left };
              })
              .filter((row) => row.quantity > 0),
          );
        })
        .finally(() => setRefundHistoryLoading(false));
    } else {
      setRefundHistory([]);
    }
  }, [showRefundModal, selectedTransaction?.id]);

  /** Default Amount Tendered to grand total whenever the cart or tax % changes. */
  useEffect(() => {
    const sub = cart.reduce((sum, item) => sum + item.quantity * (item.sale_unit_price ?? item.unit_price), 0);
    const pct = Math.max(0, Math.min(100, Number(taxPercent) || 0));
    const tax = Math.round((sub * pct) / 100 * 1000) / 1000;
    const due = sub + tax;
    if (due <= 0) {
      setAmountTenderedStr('');
      return;
    }
    setAmountTenderedStr(due.toFixed(2));
  }, [cart, taxPercent]);

  useEffect(() => {
    if (cart.length === 0) setTaxPercent(0);
  }, [cart.length]);

  // Load sellable products from the live inventory (includes expired stock qty).
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    searchDebounceRef.current = setTimeout(() => {
      void loadProducts();
    }, searchQuery.trim().length >= 2 ? 350 : 0);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedParentCategoryId, selectedSubCategoryId, categories, isOnline]);

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const loc = getUserLocationId() ?? undefined;
      const sub = selectedSubCategoryId !== 'all'
        ? categories.find((c) => c.id === selectedSubCategoryId)
        : null;
      const parent =
        selectedParentCategoryId !== 'all'
          ? categories.find((c) => c.id === selectedParentCategoryId)
          : null;
      const categoryFilterName = sub?.name || parent?.name || undefined;
      const searchTerm = searchQuery.trim().length >= 2 ? searchQuery.trim() : undefined;

      let data: POSProduct[];
      // Always prefer live API when the browser is online so Inventory and POS stock match.
      const online = typeof navigator !== 'undefined' ? navigator.onLine : isOnline;
      if (!online && loc) {
        data = await searchCachedProducts(loc, searchTerm, categoryFilterName);
        if (data.length === 0) {
          message.info('No cached products match. Connect briefly to refresh catalog.');
        }
      } else {
        data = await getPOSProducts(searchTerm, categoryFilterName, loc);
        if (loc) {
          // Refresh offline snapshot after a successful live fetch (fire-and-forget).
          void tryRefreshOfflineCatalog(loc);
        }
      }

      // Prefer real catalog items; hide seeded GST Demo* rows when other stock exists.
      const withoutSeedDemo = data.filter((p) => !/^GST Demo\b/i.test(p.name || ''));
      if (withoutSeedDemo.length > 0) {
        data = withoutSeedDemo;
      }

      // Normalize stock so expired batches with qty still count as sellable on the till.
      data = data.map((p) => ({
        ...p,
        stock_quantity: Math.max(0, Number(p.stock_quantity) || 0),
      }));

      if (selectedParentCategoryId !== 'all' && selectedSubCategoryId === 'all') {
        const childNames = categories
          .filter((c) => c.parent_id === selectedParentCategoryId)
          .map((c) => c.name);
        const allowed = new Set<string>([
          ...(parent?.name ? [parent.name] : []),
          ...childNames,
        ]);
        setProducts(data.filter((p) => !allowed.size || allowed.has(p.category || '')));
      } else {
        setProducts(data);
      }
    } catch (error) {
      message.error('Failed to load products');
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      if (!isOnline) {
        const loc = getUserLocationId();
        if (loc) {
          const cached = await searchCachedProducts(loc);
          const names = Array.from(new Set(cached.map((p) => p.category || 'Other')));
          setCategories(
            names.map((name, idx) => ({
              id: -(idx + 1),
              name,
              is_active: true,
            })) as Category[],
          );
        }
        return;
      }
      const cats = await masterDataService.getCategories({ limit: 1000 });
      // Keep active categories; API already filters active_only but be safe
      setCategories((cats || []).filter((c) => c.is_active !== false));
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  // Load stashed transactions
  const loadStashedTransactions = async () => {
    try {
      const loc = getUserLocationId() ?? undefined;
      const offlineData = await listOfflineStashes(loc);
      const serverData = isOnline ? await getStashedTransactions() : [];
      const data = [...offlineData, ...serverData].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      setStashedList(data);
      if (onStashedCountUpdate) {
        onStashedCountUpdate(data.length);
      }
    } catch (error) {
      message.error('Failed to load stashed transactions');
    }
  };

  // Load transaction history (filterable for receipt search)
  const loadTransactionHistory = async (): Promise<POSTransaction[]> => {
    try {
      const from = txnDateRange?.[0] ? txnDateRange[0].format('YYYY-MM-DD') : undefined;
      const to = txnDateRange?.[1] ? txnDateRange[1].format('YYYY-MM-DD') : undefined;
      const payment = txnPayment !== 'ALL' ? txnPayment : undefined;
      const data = await getTransactionHistory({
        transaction_id: txnSearch || undefined, // receipt search (transaction number or serial)
        from_date: from,
        to_date: to,
        payment_method: payment as any,
      });
      setTransactionList(data);
      return data;
    } catch (error) {
      message.error('Failed to load transaction history');
      return [];
    }
  };

  // Add to cart (respects stock in base units; pack/carton sell as multiples)
  const addToCart = (product: POSProduct, sellUnitOverride?: SellUnit) => {
    const stock = product.stock_quantity ?? null;
    const unitsAvailable = availableSellUnits(product);
    const sellUnit: SellUnit =
      sellUnitOverride && unitsAvailable.includes(sellUnitOverride)
        ? sellUnitOverride
        : unitsAvailable.includes(defaultSellUnit)
          ? defaultSellUnit
          : 'base';
    const unitsPer = unitsPerSellUnit(product, sellUnit);
    const listPrice = listPriceFor(product, priceMode, sellUnit);
    const isExpired = product.expiry_status === 'expired';
    // List price by default; cashier can lower the sale price in cart.
    // Admin-configured expired discount % will plug in here later.
    const salePrice = listPrice;

    if (!isOnline) {
      if (stock !== null && stock <= 0) {
        message.warning(
          `"${product.name}" shows 0 in offline cache. You can still sell — stock is verified when synced.`,
        );
      }
    } else {
      if (stock !== null && stock <= 0) {
        message.warning(`"${product.name}" is out of stock (0 units). Cannot add to cart.`);
        return;
      }
    }

    if (isExpired) {
      message.info(
        `"${product.name}" is expired — added to the sale. Set a discount on the cart line if needed.`,
      );
    } else if (product.expiry_status === 'expiring_soon') {
      message.warning(`"${product.name}" ${product.expiry_label || 'is expiring soon'}.`);
    }

    const packaging = {
      price: Number(product.price) || 0,
      wholesale_price: product.wholesale_price,
      pack_size: product.pack_size,
      carton_size: product.carton_size,
      pack_selling_price: product.pack_selling_price,
      pack_wholesale_price: product.pack_wholesale_price,
      carton_selling_price: product.carton_selling_price,
      carton_wholesale_price: product.carton_wholesale_price,
      bag_size: product.bag_size,
      bag_selling_price: product.bag_selling_price,
      bag_wholesale_price: product.bag_wholesale_price,
    };

    const existingQty =
      cart
        .filter((item) => item.product_id === product.id && (item.sell_unit || 'base') === sellUnit)
        .reduce((sum, item) => sum + toBaseQuantity(item.quantity, item.units_per || 1), 0) ?? 0;
    const wouldBeBase = existingQty + unitsPer;
    if (isOnline && stock !== null && wouldBeBase > stock) {
      message.warning(
        `Only ${stock} pcs available for "${product.name}". Cannot add another ${sellUnitLabel(sellUnit).toLowerCase()} (${unitsPer} pcs).`,
      );
      return;
    }
    if (!isOnline && stock !== null && wouldBeBase > stock) {
      message.warning(
        `Offline cache shows ${stock} pcs for "${product.name}". Adding anyway — verified on sync.`,
      );
    }

    setCart((prev) => {
      const existingItem = prev.find(
        (item) => item.product_id === product.id && (item.sell_unit || 'base') === sellUnit,
      );
      const stockQty = product.stock_quantity ?? undefined;

      if (existingItem) {
        const newQuantity = existingItem.quantity + 1;
        const sale = existingItem.sale_unit_price ?? existingItem.unit_price;
        const newBase = toBaseQuantity(newQuantity, existingItem.units_per || unitsPer);
        if (isOnline && stock !== null && newBase > stock) {
          message.warning(`Only ${stock} pcs available for "${product.name}".`);
          return prev;
        }
        return prev.map((item) => {
          if (item.id !== existingItem.id) return item;
          return {
            ...item,
            quantity: newQuantity,
            total_price: sale * newQuantity,
            stock_quantity: stockQty,
          };
        });
      }

      const newItem: POSCartItem = {
        id: `cart-${Date.now()}-${Math.random()}`,
        product_id: product.id,
        product_code: product.code,
        product_name: product.name,
        barcode: product.barcode,
        quantity: 1,
        unit_price: listPrice,
        sale_unit_price: salePrice,
        total_price: salePrice,
        stock_quantity: stockQty,
        gst_category: product.gst_category || 'taxable',
        gst_rate: product.gst_rate,
        price_mode: priceMode,
        sell_unit: sellUnit,
        units_per: unitsPer,
        packaging,
        price_locked: false,
      };
      return [...prev, newItem];
    });
  };

  const applyPriceMode = (mode: PriceMode) => {
    setPriceMode(mode);
    setCart((prev) =>
      prev.map((item) => {
        if (item.price_locked) return { ...item, price_mode: mode };
        const src = item.packaging || { price: item.unit_price };
        const sellUnit = (item.sell_unit || 'base') as SellUnit;
        const list = listPriceFor(src, mode, sellUnit);
        return {
          ...item,
          price_mode: mode,
          unit_price: list,
          sale_unit_price: list,
          total_price: list * item.quantity,
        };
      }),
    );
  };

  const setCartSellUnit = (itemId: string, sellUnit: SellUnit) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const src = item.packaging || { price: item.unit_price };
        if (!availableSellUnits(src).includes(sellUnit)) return item;
        const unitsPer = unitsPerSellUnit(src, sellUnit);
        const mode = (item.price_mode || priceMode) as PriceMode;
        const list = listPriceFor(src, mode, sellUnit);
        const baseHave = toBaseQuantity(item.quantity, item.units_per || 1);
        let newQty = Math.max(1, Math.floor(baseHave / unitsPer));
        if (newQty * unitsPer < baseHave) {
          // keep at least previous base coverage when possible
          newQty = Math.max(1, Math.ceil(baseHave / unitsPer));
        }
        const stock = item.stock_quantity ?? null;
        if (isOnline && stock !== null && toBaseQuantity(newQty, unitsPer) > stock) {
          newQty = Math.max(1, Math.floor(stock / unitsPer));
          if (newQty < 1) {
            message.warning(`Not enough stock to sell as ${sellUnitLabel(sellUnit)}.`);
            return item;
          }
        }
        const sale = item.price_locked ? item.sale_unit_price : list;
        return {
          ...item,
          sell_unit: sellUnit,
          units_per: unitsPer,
          quantity: newQty,
          unit_price: list,
          sale_unit_price: sale,
          total_price: sale * newQty,
        };
      }),
    );
  };

  // Update cart quantity (never exceeds stock in base units)
  const updateCartQuantity = (itemId: string, delta: number) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const stock = item.stock_quantity ?? null;
        const unitsPer = item.units_per || 1;
        let newQuantity = item.quantity + delta;
        const maxSell =
          stock !== null ? Math.max(1, Math.floor(stock / unitsPer)) : Infinity;
        if (stock !== null && newQuantity > maxSell) {
          newQuantity = maxSell;
          message.warning(
            `Only ${stock} pcs available for "${item.product_name}" (${maxSell} ${sellUnitLabel((item.sell_unit || 'base') as SellUnit).toLowerCase()}).`,
          );
        }
        newQuantity = Math.max(1, newQuantity);
        const sale = item.sale_unit_price ?? item.unit_price;
        return {
          ...item,
          quantity: newQuantity,
          total_price: sale * newQuantity,
        };
      }),
    );
  };

  const decreaseCartQuantity = (itemId: string) => {
    setCart((prev) => {
      const item = prev.find((i) => i.id === itemId);
      if (!item) return prev;
      if (item.quantity <= 1) {
        message.info('Item removed from cart');
        return prev.filter((i) => i.id !== itemId);
      }
      return prev.map((i) => {
        if (i.id !== itemId) return i;
        const newQuantity = i.quantity - 1;
        const sale = i.sale_unit_price ?? i.unit_price;
        return { ...i, quantity: newQuantity, total_price: sale * newQuantity };
      });
    });
  };

  // Remove from cart
  const removeFromCart = (itemId: string) => {
    setCart((prev) => prev.filter((item) => item.id !== itemId));
    message.info('Item removed from cart');
  };

  const setLineSaleUnitPrice = (itemId: string, saleRaw: number | null) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        let sale = Number(saleRaw);
        if (!Number.isFinite(sale) || sale < 0) sale = item.unit_price;
        const qty = item.quantity;
        const locked = Math.abs(sale - item.unit_price) > 0.0005;
        return { ...item, sale_unit_price: sale, total_price: sale * qty, price_locked: locked };
      })
    );
  };

  /** Look up by barcode/SKU and add one line to cart (stock rules apply). */
  const lookupBarcodeAndAdd = async (raw: string): Promise<boolean> => {
    const code = raw.trim();
    if (!code) return false;
    try {
      const loc = getUserLocationId() ?? undefined;
      let product: POSProduct | null = null;
      if (!isOnline && loc) {
        product = await findCachedProductByBarcode(loc, code);
      } else {
        product = await getProductByBarcode(code, loc);
      }
      if (product) {
        addToCart(product);
        return true;
      }
      message.warning(
        isOnline
          ? 'No product found for this barcode or SKU'
          : 'Not in offline catalog — connect to refresh products',
      );
      return false;
    } catch (error) {
      message.error('Failed to find product');
      return false;
    }
  };

  /** Modal / explicit barcode field — requires non-empty input. */
  const handleBarcodeSubmit = async (): Promise<boolean> => {
    if (!barcodeInput.trim()) {
      message.warning('Enter or scan a barcode');
      return false;
    }
    const ok = await lookupBarcodeAndAdd(barcodeInput);
    if (ok) {
      setBarcodeInput('');
      setSearchQuery('');
      void loadProducts();
    }
    return ok;
  };

  /**
   * Search Enter / USB scanner: resolve barcode or SKU from inventory first, else run name search.
   */
  const handleProductSearchEnter = async () => {
    const q = searchQuery.trim();
    if (!q) return;

    const looksLikeBarcodeOrSku =
      /^\d{4,}$/.test(q) || (/^[A-Za-z0-9-]+$/.test(q) && q.length >= 4);
    if (looksLikeBarcodeOrSku) {
      const ok = await lookupBarcodeAndAdd(q);
      if (ok) {
        setSearchQuery('');
        setProducts([]);
        return;
      }
    }

    if (q.length < 2) {
      message.info('Type at least 2 characters to search products');
      return;
    }

    await loadProducts();
  };

  useEffect(() => {
    if (cart.length === 0) setChargeOpen(false);
  }, [cart.length]);
  const listSubtotal = cart.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const totalDue = cart.reduce((sum, item) => sum + item.quantity * (item.sale_unit_price ?? item.unit_price), 0);
  const lineDiscountTotal = Math.max(0, listSubtotal - totalDue);
  const gstEnabled = Boolean(taxSettings?.gst_registered);
  const gstInclusive = taxSettings?.gst_prices_inclusive !== false;
  const gstRate = Number(taxSettings?.standard_rate ?? DEFAULT_GST_RATE);
  const gstPreview = gstEnabled
    ? cart.reduce((sum, item) => {
        const charged = item.quantity * (item.sale_unit_price ?? item.unit_price);
        return sum + computeLineGst(charged, item.gst_category, gstInclusive, gstRate).gst_amount;
      }, 0)
    : 0;
  const gstTaxablePreview = gstEnabled
    ? cart.reduce((sum, item) => {
        const charged = item.quantity * (item.sale_unit_price ?? item.unit_price);
        return sum + computeLineGst(charged, item.gst_category, gstInclusive, gstRate).taxable_value;
      }, 0)
    : 0;
  const gstExemptPreview = gstEnabled
    ? cart.reduce((sum, item) => {
        const charged = item.quantity * (item.sale_unit_price ?? item.unit_price);
        const line = computeLineGst(charged, item.gst_category, gstInclusive, gstRate);
        return normalizeGstCategory(item.gst_category) === 'exempt' ? sum + line.exempt_value : sum;
      }, 0)
    : 0;
  const gstZeroRatedPreview = gstEnabled
    ? cart.reduce((sum, item) => {
        const charged = item.quantity * (item.sale_unit_price ?? item.unit_price);
        const line = computeLineGst(charged, item.gst_category, gstInclusive, gstRate);
        return normalizeGstCategory(item.gst_category) === 'zero_rated' ? sum + line.exempt_value : sum;
      }, 0)
    : 0;
  const taxRateApplied = gstEnabled ? 0 : Math.max(0, Math.min(100, Number(taxPercent) || 0));
  const taxAmount = gstEnabled
    ? gstPreview
    : totalDue > 0
      ? Math.round(((totalDue * taxRateApplied) / 100) * 1000) / 1000
      : 0;
  const grandTotal = gstEnabled ? (gstInclusive ? totalDue : totalDue + gstPreview) : totalDue + taxAmount;
  const buyerPayload = gstEnabled
    ? {
        buyer_name: buyerName.trim() || undefined,
        buyer_cid: buyerCid.trim() || undefined,
        buyer_tpn: buyerTpn.trim() || undefined,
        buyer_address: buyerAddress.trim() || undefined,
      }
    : {};
  const resetBuyerFields = () => {
    setBuyerName('');
    setBuyerCid('');
    setBuyerTpn('');
    setBuyerAddress('');
  };
  const tenderedNum = parseFloat(amountTenderedStr) || 0;
  const changeDue = Math.max(0, tenderedNum - grandTotal);

  const keypadDigit = (ch: string) => {
    if (ch === 'back') {
      setAmountTenderedStr((s) => s.slice(0, -1));
      return;
    }
    if (ch === 'exact') {
      setAmountTenderedStr(grandTotal > 0 ? grandTotal.toFixed(2) : '');
      return;
    }
    if (ch === '.' && amountTenderedStr.includes('.')) return;
    setAmountTenderedStr((s) => s + ch);
  };

  const lowStockCount = products.filter((p) => (p.stock_quantity ?? 0) > 0 && (p.stock_quantity ?? 0) <= 2).length;
  const outOfStockCount = products.filter((p) => (p.stock_quantity ?? 0) <= 0).length;
  const totalUnitsInStock = products.reduce((sum, p) => sum + (p.stock_quantity ?? 0), 0);

  const handleSplitToggle = (enabled: boolean) => {
    setSplitPayment(enabled);
    if (enabled) {
      setSplitLines([
        createSplitPaymentLine(POSPaymentMethod.MOBILE_TRANSFER),
        createSplitPaymentLine(POSPaymentMethod.CASH),
      ]);
    } else {
      setSplitLines([]);
    }
  };

  // Handle complete order — pass true to print, false to save record only
  const handleCompleteOrder = (shouldPrint?: boolean) => {
    const wantPrint = typeof shouldPrint === 'boolean' ? shouldPrint : printIntentRef.current;
    printIntentRef.current = wantPrint;
    if (wantPrint !== printReceipt) {
      handlePrintReceiptChange(wantPrint);
    }
    if (cart.length === 0) {
      message.warning('Cart is empty');
      return;
    }
    if (gstEnabled && !String(taxSettings?.tax_id || '').trim()) {
      message.error('Cannot complete a GST sale without TPN. Ask admin to save Taxpayer Number in GST settings.');
      return;
    }
    if (!isOnline) {
      if (splitPayment) {
        message.warning('Split payment is not available in offline mode. Use Cash or Card.');
        return;
      }
      if (paymentMethod === POSPaymentMethod.CREDIT) {
        message.warning('Credit sales need internet. Use Cash or Card while offline.');
        return;
      }
    }
    if (paymentMethod === POSPaymentMethod.CREDIT && !splitPayment && !creditCustomerName.trim()) {
      message.warning('Enter the customer name for a credit sale');
      return;
    }
    if (splitPayment) {
      const splitNeedsPhone = splitLines.some(
        (line) => line.method === POSPaymentMethod.MOBILE_TRANSFER,
      );
      if (splitNeedsPhone) {
        const phoneErr = validatePayerPhone(paymentPayerPhone);
        if (phoneErr) {
          message.warning(phoneErr);
          return;
        }
      }
      const splitError = validateSplitPaymentLines(splitLines, grandTotal);
      if (splitError) {
        message.warning(splitError);
        return;
      }
      void processPayment('', splitNeedsPhone ? paymentPayerPhone : '');
      return;
    }
    if (paymentMethod === POSPaymentMethod.MOBILE_TRANSFER) {
      setShowPaymentModal(true);
    } else if (paymentMethod === POSPaymentMethod.CASH) {
      // Cash: no phone / reference capture
      void processPayment('', '');
    } else {
      // Card / other: phone optional
      void processPayment('', paymentPayerPhone);
    }
  };

  // Process payment
  const processPayment = async (reference: string, payerPhone: string = '') => {
    const shouldPrint = printIntentRef.current;
    if (cart.length === 0) {
      message.warning('Cart is empty');
      return;
    }
    if (gstEnabled && !String(taxSettings?.tax_id || '').trim()) {
      message.error('Cannot complete a GST sale without TPN. Ask admin to save Taxpayer Number in GST settings.');
      return;
    }
    const locationId = getUserLocationId();
    if (!locationId) {
      message.error('No location assigned to your account. Please contact your administrator.');
      return;
    }

    if (!isOnline) {
      setLoading(true);
      const cartSnapshot = cart.map((item) => ({ ...item }));
      try {
        const localId = createOfflineLocalId();
        const createdAt = new Date().toISOString();
        const user = RBACService.getCurrentUser();
        const servedBy = user?.id ? Number(user.id) : undefined;
        const saleItems = cart.map((item) => {
          const charged =
            item.sale_unit_price != null && Number.isFinite(item.sale_unit_price) && item.sale_unit_price >= 0
              ? item.sale_unit_price
              : item.unit_price;
          const unitsPer = Math.max(1, Math.floor(Number(item.units_per) || 1));
          const sellQty = Math.max(1, Math.floor(Number(item.quantity) || 1));
          const baseQty = sellQty * unitsPer;
          const lineTotal = sellQty * charged;
          const unitPerBase = baseQty > 0 ? Math.round((lineTotal / baseQty) * 1000) / 1000 : charged;
          return {
            product_id: item.product_id,
            product_code: item.product_code,
            product_name: item.product_name,
            barcode: item.barcode,
            quantity: baseQty,
            unit_price: unitPerBase,
            total_price: lineTotal,
          };
        });
        await enqueueOfflineSale({
          local_id: localId,
          created_at: createdAt,
          location_id: locationId,
          served_by: servedBy,
          items: saleItems,
          payment_method: paymentMethod,
          payment_reference: reference || null,
          payer_phone: payerPhone || null,
          receipt_printed: shouldPrint,
          tax_amount: taxAmount,
          total_amount: grandTotal,
          status: 'pending',
          ...buyerPayload,
        });
        for (const item of saleItems) {
          await adjustCachedStock(locationId, item.product_id, -item.quantity);
        }
        const receiptTxn = {
          ...offlineSaleToReceiptTransaction(
            {
              local_id: localId,
              created_at: createdAt,
              location_id: locationId,
              served_by: servedBy,
              items: saleItems,
              payment_method: paymentMethod,
              payment_reference: reference || null,
              payer_phone: payerPhone || null,
              receipt_printed: shouldPrint,
              tax_amount: taxAmount,
              total_amount: grandTotal,
              status: 'pending',
              ...buyerPayload,
            },
            user?.name || user?.email,
          ),
          is_tax_invoice: gstEnabled,
          tax_invoice_number: gstEnabled ? `OFFLINE-${localId}` : null,
          taxable_amount: gstEnabled ? gstTaxablePreview : null,
          exempt_amount: gstEnabled ? gstExemptPreview : null,
          zero_rated_amount: gstEnabled ? gstZeroRatedPreview : null,
          buyer_name: buyerName.trim() || null,
          buyer_cid: buyerCid.trim() || null,
          buyer_tpn: buyerTpn.trim() || null,
          buyer_address: buyerAddress.trim() || null,
          supplier_name: taxSettings?.legal_name || null,
          supplier_tpn: taxSettings?.tax_id || null,
          supplier_address: taxSettings?.address || null,
        };
        message.success('Sale saved offline — will sync when internet returns');
        if (shouldPrint) {
          const printed = printPosReceipt(receiptTxn, posLocationLabel);
          if (!printed) {
            message.warning('Could not open receipt preview.');
          }
        } else {
          message.info('No receipt printed — sale kept on record');
        }
        setCart([]);
        setAmountTenderedStr('');
        setCouponInput('');
        setTaxPercent(0);
        resetBuyerFields();
        setOrderDisplayId((n) => n + 1);
        setShowPaymentModal(false);
        setPaymentReference('');
        setPaymentPayerPhone('');
        setPaymentMethod(POSPaymentMethod.CASH);
        setCreditCustomerName('');
        setSplitPayment(false);
        setSplitLines([]);
        await refreshPendingCount();
      } catch (error: any) {
        message.error(error?.message || 'Failed to save offline sale');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    const cartSnapshot = cart.map((item) => ({ ...item }));
    try {
      const splitSnapshot =
        splitPayment && splitLines.length >= 2
          ? splitLines.map((line) => ({
              payment_method: String(line.method),
              amount: parseFloat(line.amount),
              reference_number: line.reference.trim() || undefined,
              payer_phone: paymentPayerPhone.trim() || line.phone?.trim() || undefined,
            }))
          : null;

      let transaction = await completePOSTransaction(
        cart,
        splitPayment
          ? null
          : {
              method: paymentMethod,
              amount: grandTotal,
              reference_number: reference || undefined,
              payer_phone: paymentPayerPhone.trim() || payerPhone || undefined,
            },
        locationId,
        {
          tax_amount: taxAmount,
          payment_details: splitPayment
            ? splitLines.map((line) => ({
                payment_method: line.method,
                amount: parseFloat(line.amount),
                reference_number: line.reference.trim() || undefined,
                payer_phone: paymentPayerPhone.trim() || line.phone?.trim() || undefined,
              }))
            : undefined,
          payer_phone: paymentPayerPhone.trim() || payerPhone || undefined,
          receipt_printed: shouldPrint,
          credit_customer_name:
            !splitPayment && paymentMethod === POSPaymentMethod.CREDIT
              ? creditCustomerName.trim()
              : undefined,
          ...buyerPayload,
        },
      );

      if (splitSnapshot && splitSnapshot.length > 1) {
        if (!transaction.payment_details?.length) {
          transaction = {
            ...transaction,
            payment_method: POSPaymentMethod.SPLIT,
            payment_details: splitSnapshot,
          };
        }
        saveSplitPaymentCache(transaction.id, transaction.payment_details || splitSnapshot);
      }
      message.success(`Transaction ${transaction.transaction_number} completed successfully!`);
      if (shouldPrint) {
        const printTxn = {
          ...transaction,
          items: cartSnapshot.map((item, idx) => ({
            ...item,
            id: item.id || `line-${idx}`,
          })),
          is_tax_invoice: transaction.is_tax_invoice ?? gstEnabled,
          supplier_name: transaction.supplier_name || taxSettings?.legal_name,
          supplier_tpn: transaction.supplier_tpn || taxSettings?.tax_id,
          supplier_address: transaction.supplier_address || taxSettings?.address,
        };
        const printed = printPosReceipt(
          printTxn,
          formatPOSTransactionLocation(transaction) || posLocationLabel,
        );
        if (!printed) {
          message.warning('Could not open receipt preview. Use PRINT on the transaction.');
        }
      } else {
        message.info('No receipt printed — sale kept on record');
      }
      setCart([]);
      setAmountTenderedStr('');
      setCouponInput('');
      setTaxPercent(0);
      resetBuyerFields();
      setOrderDisplayId((n) => n + 1);
      setShowPaymentModal(false);
      setPaymentReference('');
      setPaymentPayerPhone('');
      setPaymentMethod(POSPaymentMethod.CASH); // Reset to default
      setCreditCustomerName('');
      setSplitPayment(false);
      setSplitLines([]);
      void loadProducts();
      const history = await loadTransactionHistory();
      if (transaction.payment_details && transaction.payment_details.length > 1) {
        setTransactionList((prev) => {
          const merged = prev.map((t) =>
            t.id === transaction.id ? { ...t, ...transaction } : t,
          );
          if (!merged.some((t) => t.id === transaction.id)) {
            return [transaction, ...history];
          }
          return merged;
        });
      }
      if (onTransactionComplete) {
        onTransactionComplete(transaction);
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      const detail = error?.response?.data?.detail;
      const msg =
        (typeof detail === 'string' && detail.trim() ? detail.trim() : null) ||
        (typeof error?.response?.data?.message === 'string' && error.response.data.message.trim() ? error.response.data.message.trim() : null) ||
        (typeof error?.message === 'string' && error.message.trim() ? error.message.trim() : null) ||
        'Failed to complete transaction. Please try again.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Handle suspend
  const handleSuspend = async () => {
    if (cart.length === 0) {
      message.warning('Cart is empty');
      return;
    }
    setLoading(true);
    try {
      const locationId = getUserLocationId();
      if (!locationId) {
        message.error('No location assigned to your account. Please contact your administrator.');
        return;
      }
      let stashed: POSStashedTransaction;
      if (isOnline) {
        stashed = await stashTransaction(cart);
      } else {
        const localStash = createOfflineStash(
          locationId,
          cart.map((item) => ({ ...item })),
        );
        await saveOfflineStash(localStash);
        stashed = localStash;
      }
      message.success(`Transaction suspended successfully! Serial: ${stashed.serial}`);
      setCart([]); // Clear cart after suspending
      await loadStashedTransactions(); // Reload stashed list to show the new transaction
      // If we're on cart view, we could optionally switch to stashed view
      // But let's keep it on cart view so user can continue adding items
    } catch (error) {
      console.error('Suspend error:', error);
      message.error('Failed to suspend transaction');
    } finally {
      setLoading(false);
    }
  };

  // Handle void sale
  const handleVoidSale = () => {
    Modal.confirm({
      title: 'Void Sale',
      content: 'Are you sure you want to void this sale? All items will be removed from the cart.',
      onOk: () => {
        setCart([]);
        message.info('Sale voided');
      },
    });
  };

  // Handle retrieve stashed (remove from hold list after retrieving)
  const handleRetrieveStashed = async (stashed: POSStashedTransaction) => {
    setLoading(true);
    try {
      if (stashed.id.startsWith('offline-stash-')) {
        await deleteOfflineStash(stashed.id);
      } else {
        await deleteStashedTransaction(stashed.id);
      }

      // Map stashed items to cart items with new IDs
      const cartItems = stashed.items.map(item => ({
        ...item,
        id: `cart-${Date.now()}-${Math.random()}`,
      }));
      setCart(cartItems);
      setSelectedStashed(null);
      await loadStashedTransactions();

      // Switch to cart view to continue the purchase
      if (onViewChange) {
        onViewChange('cart');
      }
      message.success(`Transaction ${stashed.serial} retrieved to cart`);
    } catch (error) {
      console.error('Retrieve stashed error:', error);
      message.error('Failed to retrieve stashed cart');
    } finally {
      setLoading(false);
    }
  };

  // Handle view transaction (fetch full record including split payment lines)
  const handleViewTransaction = async (transaction: POSTransaction) => {
    setSelectedTransaction(transaction);
    try {
      const full = await getTransactionById(transaction.id);
      if (full) {
        setSelectedTransaction(full);
        setTransactionList((prev) =>
          prev.map((t) => (t.id === full.id ? { ...t, ...full } : t)),
        );
      }
    } catch {
      // Keep list row data if detail fetch fails
    }
  };

  // Handle refund
  const handleRefund = async () => {
    if (!selectedTransaction || refundItems.length === 0) {
      message.warning('Please select items to refund');
      return;
    }
    setLoading(true);
    try {
      await refundTransaction(selectedTransaction.id, refundItems);
      message.success('Refund processed');
      setShowRefundModal(false);
      setRefundItems([]);
      setRefundHistory([]);
      setSelectedTransaction(null);
      void loadProducts();
      loadTransactionHistory();
    } catch (error: any) {
      const raw = error?.response?.data?.detail ?? error?.message;
      const msg = (typeof raw === 'string' && raw.trim() ? raw.trim() : null) || 'Failed to process refund';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };


  const totalCartQty = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartLineCount = cart.length;
  /** Per product: base pcs reserved in cart + a cart line id for the list stepper. */
  const cartQtyByProductId = useMemo(() => {
    const map = new Map<number, { qty: number; baseQty: number; cartId: string }>();
    cart.forEach((item) => {
      const base = toBaseQuantity(item.quantity, item.units_per || 1);
      const prev = map.get(item.product_id);
      if (!prev) {
        map.set(item.product_id, { qty: item.quantity, baseQty: base, cartId: item.id });
        return;
      }
      const preferThis = (item.units_per || 1) === 1;
      map.set(item.product_id, {
        // Stepper shows sell qty of the preferred (pcs) line when mixed UOM exists
        qty: preferThis ? item.quantity : prev.qty,
        baseQty: prev.baseQty + base,
        cartId: preferThis ? item.id : prev.cartId,
      });
    });
    return map;
  }, [cart]);
  const posLocationLabel =
    (RBACService.getCurrentUser()?.locationName as string | undefined)?.trim() || 'Store';

  const renderProductSearchToolbar = () => (
    <>
      <div
        className={`pos-offline-banner ${isOnline ? 'pos-offline-banner--online' : 'pos-offline-banner--offline'}`}
        role="status"
        aria-live="polite"
      >
        {!isOnline && <span className="pos-offline-banner__dot" aria-hidden />}
        {!isOnline && (
          <span className="pos-offline-banner__text">
            Offline mode — sales save locally
            {pendingOfflineCount > 0 ? ` · ${pendingOfflineCount} pending sync` : ''}
            {offlineSyncing ? ' · Syncing…' : ''}
          </span>
        )}
        {isOnline && pendingOfflineCount > 0 && (
          <span className="pos-offline-banner__text">
            {pendingOfflineCount} sale{pendingOfflineCount === 1 ? '' : 's'} waiting to sync
            {offlineSyncing ? ' · Syncing…' : ''}
          </span>
        )}
        {isOnline && (
          <Button
            type="link"
            size="small"
            className="pos-offline-banner__action"
            loading={offlineSyncing}
            onClick={() => {
              const loc = getUserLocationId();
              if (loc) {
                void tryRefreshOfflineCatalog(loc).then(async () => {
                  const meta = await getCatalogMeta(loc);
                  setCatalogSyncedAt(meta?.synced_at ?? null);
                  await loadProducts();
                  message.success('Product list refreshed — stock matches inventory');
                });
              } else {
                void loadProducts();
              }
              void syncOfflineSales();
            }}
          >
            Refresh & sync
          </Button>
        )}
        {catalogSyncedAt && (
          <span className="pos-offline-banner__meta">
            Catalog: {moment(catalogSyncedAt).fromNow()}
          </span>
        )}
      </div>

      <div className="pos-product-search-wrap">
        <div className="pos-product-search-row">
          <Input
            className="app-search pos-search-input"
            placeholder="Search for product or scan barcode (Enter)"
            prefix={<PosIcon name="search" size={17} className="pos-search-input-icon" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={() => void handleProductSearchEnter()}
            size="large"
            allowClear
          />
        </div>
      </div>

      <div className="pos-catBlock" style={{ marginBottom: 10 }}>
        <div className="pos-catRow">
          <Button
            size="small"
            className={`pos-catPill ${selectedParentCategoryId === 'all' ? 'is-active' : ''}`}
            onClick={() => {
              setSelectedParentCategoryId('all');
              setSelectedSubCategoryId('all');
            }}
          >
            All
          </Button>
          {posParentCategories.map((c) => (
              <Button
                key={c.id}
                size="small"
                className={`pos-catPill ${selectedParentCategoryId === c.id ? 'is-active' : ''}`}
                onClick={() => {
                  setSelectedParentCategoryId(c.id);
                  setSelectedSubCategoryId('all');
                }}
              >
                {c.name}
              </Button>
            ))}
        </div>
        {selectedParentCategoryId !== 'all' && categories.some((c) => c.parent_id === selectedParentCategoryId) && (
          <div className="pos-catRow pos-catRow--sub">
            <Button
              size="small"
              className={`pos-catPill ${selectedSubCategoryId === 'all' ? 'is-active' : ''}`}
              onClick={() => setSelectedSubCategoryId('all')}
            >
              All
            </Button>
            {categories
              .filter((c) => c.parent_id === selectedParentCategoryId)
              .filter((c) => !isGstDemoLabel(c.name))
              .map((c) => (
                <Button
                  key={c.id}
                  size="small"
                  className={`pos-catPill ${selectedSubCategoryId === c.id ? 'is-active' : ''}`}
                  onClick={() => setSelectedSubCategoryId(c.id)}
                >
                  {c.name}
                </Button>
              ))}
          </div>
        )}
      </div>
    </>
  );

  const renderProductResults = () => {
    if (productsLoading) {
      return (
        <div className="pos-product-results-panel">
          <div className="pos-product-empty">
            <Spin size="small" />
            <div className="pos-product-empty-title">Loading inventory…</div>
          </div>
        </div>
      );
    }
    if (products.length === 0) {
      return (
        <div className="pos-product-results-panel">
          <div className="pos-product-empty">
            <PosIconBadge name="packageSearch" tone="slate" size={22} />
            <div className="pos-product-empty-title">No products found</div>
            <div className="pos-product-empty-desc">
              {isSearchNarrowed
                ? 'Try a different name, SKU, or barcode'
                : 'Add stock via Goods Receipt, or check the selected location'}
            </div>
          </div>
        </div>
      );
    }

    const visibleProducts = products.slice(0, 48);
    const hiddenCount = products.length - visibleProducts.length;

    return (
      <div className="pos-product-results-panel">
        <div className="pos-product-results-head">
          <span className="pos-product-results-label">
            {isSearchNarrowed ? 'Search results' : 'Your products'}
          </span>
          <span className="pos-product-results-count">
            {products.length} product{products.length === 1 ? '' : 's'}
            {hiddenCount > 0 ? ` · showing ${visibleProducts.length}` : ''}
          </span>
        </div>
        <div className="pos-product-list">
        {visibleProducts.map((product, index) => {
          const stockQty = Math.max(0, Number(product.stock_quantity) || 0);
          const cartEntry = cartQtyByProductId.get(product.id);
          const inCartQty = cartEntry?.qty ?? 0;
          const inCartBase = cartEntry?.baseQty ?? 0;
          const remaining = Math.max(0, stockQty - inCartBase);
          const inCart = inCartBase > 0;
          const expired = product.expiry_status === 'expired';
          const expiring = product.expiry_status === 'expiring_soon';
          const warehouseOut = stockQty <= 0;
          const out = warehouseOut || remaining <= 0;
          const low = !out && remaining <= 2;
          const listPrice = Number(product.price) || 0;
          const displayPrice = listPrice;
          const stockNote = warehouseOut
            ? 'Out of stock'
            : remaining <= 0
              ? 'All in this sale'
              : [
                  `${remaining.toLocaleString()} available`,
                  expired ? 'Expired · sellable' : expiring ? (product.expiry_label || 'Expiring') : '',
                ]
                  .filter(Boolean)
                  .join(' · ');
          const stockClass = warehouseOut || remaining <= 0
            ? ' pos-stock--out'
            : expired || expiring || low
              ? ' pos-stock--low'
              : '';
          return (
            <div
              key={product.id}
              className={`pos-product-row${inCart ? ' pos-product-row--in-cart' : ''}${out ? ' pos-product-row--disabled' : ''}${expired ? ' pos-product-row--clearance' : ''}`}
              style={{ animationDelay: `${index * 45}ms` }}
              role="button"
              tabIndex={out ? -1 : 0}
              aria-disabled={out}
              aria-label={
                warehouseOut
                  ? `${product.name} — out of stock`
                  : expired
                    ? `Add expired ${product.name} (${remaining} available) — discount in cart if needed`
                    : inCart
                      ? `${product.name}, ${inCartBase} in sale, ${remaining} available`
                      : `Add ${product.name} to sale`
              }
              onClick={() => {
                if (!out) addToCart(product);
              }}
              onKeyDown={(e) => {
                if (out) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  addToCart(product);
                }
              }}
            >
              <div className="pos-product-row-body">
                <div className="pos-product-row-name" title={product.name}>
                  {product.name}
                </div>
                <div className={`pos-product-row-stock${stockClass}`}>
                  {stockNote}
                </div>
              </div>
              <div className="pos-product-row-side">
                <div className="pos-product-row-price app-mono">
                  <span className="pos-product-row-currency">Nu.</span>
                  {displayPrice.toFixed(2)}
                </div>
                {inCart ? (
                  <span className="pos-product-row-incart">{inCartQty} in sale</span>
                ) : null}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  const renderPaymentPanel = () => (
    <Card
      title={
        <div className="pos-checkout-card-head">
          <span className="pos-checkout-card-title">
            <PosIconBadge name="wallet" tone="blue" size={18} />
            Payment Method
          </span>
          <label className="pos-checkout-split-toggle">
            <span className="pos-checkout-split-label">Split</span>
            <Switch
              checked={splitPayment}
              onChange={handleSplitToggle}
              size="small"
              aria-label="Toggle split payment"
            />
          </label>
        </div>
      }
      className={`pos-column-card pos-column-card--checkout${splitPayment ? ' pos-column-card--split-checkout' : ''}`}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0, overflow: 'hidden' }}
    >
      <div
        className="pos-checkout-scroll-body"
        ref={setCheckoutScrollNode}
        tabIndex={0}
        onMouseEnter={() => checkoutScrollRef.current?.focus({ preventScroll: true })}
      >
      <div className="pos-coupon-row">
        <Space.Compact className="pos-coupon-compact" style={{ width: '100%' }}>
          <Input
            className="pos-coupon-input"
            placeholder="Coupon or discount code"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            onPressEnter={() => message.info('Coupons are not enabled yet')}
          />
          <Button type="default" className="pos-coupon-apply" onClick={() => message.info('Coupons are not enabled yet')}>
            Apply
          </Button>
        </Space.Compact>
      </div>

      <div className={`pos-tendered-section${paymentMethod === POSPaymentMethod.CASH && !splitPayment ? '' : ' pos-tendered-section--hidden'}`}>
        <div className="pos-amount-tendered-box">
          <span className="pos-amount-tendered-label">Amount Tendered</span>
          <span className="pos-amount-tendered-value app-mono">{amountTenderedStr || '0.000'}</span>
        </div>
        <div className="pos-keypad">
          {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((ch) => (
            <Button key={ch} className="pos-keypad-btn" onClick={() => keypadDigit(ch)}>
              {ch}
            </Button>
          ))}
          <Button className="pos-keypad-btn pos-keypad-btn--ghost" onClick={() => keypadDigit('exact')}>
            Exact
          </Button>
          <Button className="pos-keypad-btn" onClick={() => keypadDigit('0')}>
            0
          </Button>
          <Button className="pos-keypad-btn pos-keypad-btn--back" onClick={() => keypadDigit('back')}>
            ⌫
          </Button>
        </div>
        {tenderedNum > 0 && (
          <Text type={changeDue >= 0 ? 'success' : 'danger'} className="app-mono" style={{ display: 'block', marginTop: 8 }}>
            {changeDue >= 0 ? `Change: Nu. ${changeDue.toFixed(2)}` : `Short: Nu. ${Math.abs(tenderedNum - grandTotal).toFixed(2)}`}
          </Text>
        )}
      </div>

      <Card
        size="small"
        className={`pos-payment-card${splitPayment ? ' pos-payment-card--split' : ''}`}
        style={{ marginTop: 14 }}
        bodyStyle={{ padding: '12px 14px' }}
      >
        {splitPayment ? (
          <SplitPaymentEditor
            lines={splitLines}
            onChange={setSplitLines}
            orderTotal={grandTotal}
            disabled={loading}
            mobilePayAccount={{
              legalName: taxSettings?.legal_name,
              accountName: taxSettings?.payment_account_name,
              accountNumber: taxSettings?.payment_account_number,
              bankName: taxSettings?.payment_account_bank,
            }}
          />
        ) : (
        <>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <Button
            type={priceMode === 'retail' ? 'primary' : 'default'}
            size="small"
            onClick={() => applyPriceMode('retail')}
          >
            Retail
          </Button>
          <Button
            type={priceMode === 'wholesale' ? 'primary' : 'default'}
            size="small"
            onClick={() => applyPriceMode('wholesale')}
          >
            Wholesale
          </Button>
          <Select
            className="pos-unit-select"
            dropdownClassName="pos-unit-select-dropdown"
            value={defaultSellUnit}
            onChange={(v) => setDefaultSellUnit(v as SellUnit)}
            aria-label="Default sell unit"
            options={[
              { value: 'base', label: 'Pcs' },
              { value: 'pack', label: 'Pack' },
              { value: 'carton', label: 'Carton' },
              { value: 'bag', label: 'Bag' },
            ]}
          />
        </div>
        <Row gutter={[6, 8]} style={{ margin: 0 }}>
          <Col span={8}>
            <Button
              type={paymentMethod === POSPaymentMethod.CASH ? 'primary' : 'default'}
              block
              size="large"
              className="pos-pay-method-btn"
              onClick={() => {
                setPaymentMethod(POSPaymentMethod.CASH);
                setPaymentPayerPhone('');
              }}
            >
              <CashPayIcon className="pos-pay-method-art pos-pay-method-art--compact" />
              <span>Cash</span>
            </Button>
          </Col>
          <Col span={8}>
            <Button
              type={paymentMethod === POSPaymentMethod.CARD_PAYMENT ? 'primary' : 'default'}
              block
              size="large"
              className="pos-pay-method-btn"
              onClick={() => setPaymentMethod(POSPaymentMethod.CARD_PAYMENT)}
            >
              <ManualCardPayIcon className="pos-pay-method-art pos-pay-method-art--compact" />
              <span>Card</span>
            </Button>
          </Col>
          <Col span={8}>
            <Button
              type={paymentMethod === POSPaymentMethod.MOBILE_TRANSFER ? 'primary' : 'default'}
              block
              size="large"
              className="pos-pay-method-btn"
              onClick={() => setPaymentMethod(POSPaymentMethod.MOBILE_TRANSFER)}
            >
              <MobilePayIcon className="pos-pay-method-art pos-pay-method-art--compact" />
              <span>Mobile</span>
            </Button>
          </Col>
          <Col span={8}>
            <Button
              type={paymentMethod === POSPaymentMethod.CREDIT ? 'primary' : 'default'}
              block
              size="large"
              className="pos-pay-method-btn"
              onClick={() => {
                setPaymentMethod(POSPaymentMethod.CREDIT);
                setSplitPayment(false);
              }}
            >
              <span>Credit</span>
            </Button>
          </Col>
        </Row>
        </>
        )}
      </Card>

      {paymentMethod === POSPaymentMethod.CASH && !splitPayment ? null : (
      <div className="pos-checkout-customer-phone">
        {(paymentMethod !== POSPaymentMethod.CREDIT ||
          (splitPayment &&
            splitLines.some((line) => line.method === POSPaymentMethod.MOBILE_TRANSFER))) && (
          <>
            <Text strong className="pos-checkout-customer-phone-label">
              Customer phone
              {paymentMethod === POSPaymentMethod.MOBILE_TRANSFER ||
              (splitPayment &&
                splitLines.some((line) => line.method === POSPaymentMethod.MOBILE_TRANSFER)) ? (
                <span style={{ color: '#ff4d4f' }}> *</span>
              ) : null}
            </Text>
            <Input
              size="large"
              placeholder={
                paymentMethod === POSPaymentMethod.MOBILE_TRANSFER
                  ? 'e.g. 17123456 — required for mobile pay'
                  : 'e.g. 17123456'
              }
              value={paymentPayerPhone}
              onChange={(e) => setPaymentPayerPhone(e.target.value)}
              inputMode="tel"
              allowClear
              status={
                (paymentMethod === POSPaymentMethod.MOBILE_TRANSFER ||
                  (splitPayment &&
                    splitLines.some((line) => line.method === POSPaymentMethod.MOBILE_TRANSFER))) &&
                (!paymentPayerPhone.trim() || countPhoneDigits(paymentPayerPhone) < 7)
                  ? 'error'
                  : undefined
              }
            />
            <Text type="secondary" className="pos-checkout-customer-phone-hint">
              {paymentMethod === POSPaymentMethod.MOBILE_TRANSFER
                ? 'Required for mobile / bank transfer'
                : 'Not needed for cash — used for card / mobile if useful'}
            </Text>
          </>
        )}
        {paymentMethod === POSPaymentMethod.CREDIT && (
          <div style={{ marginTop: 8 }}>
            <Text strong>
              Credit customer name <span style={{ color: '#ff4d4f' }}>*</span>
            </Text>
            <Input
              size="large"
              placeholder="Name on the account"
              value={creditCustomerName}
              onChange={(e) => setCreditCustomerName(e.target.value)}
              style={{ marginTop: 4 }}
            />
            <Button type="link" style={{ paddingLeft: 0 }} onClick={() => setCreditBookOpen(true)}>
              Open credit book
            </Button>
          </div>
        )}
      </div>
      )}

      <div className="pos-checkout-print-row">
        <PosIconBadge name="printer" tone="orange" variant="tile" size={28} />
        <div className="pos-print-receipt-copy">
          <span>Customer receipt</span>
          <span className="pos-print-receipt-hint">
            Some customers do not need a slip — use Complete only to keep the sale on record
          </span>
        </div>
      </div>

      <div className="pos-checkout-footer-actions pos-checkout-footer-actions--with-dual-complete">
        <Button
          block
          type="default"
          className="pos-action-btn pos-action-btn--void"
          onClick={handleVoidSale}
          size="large"
          disabled={cart.length === 0}
        >
          VOID SALE
        </Button>
        <Button
          block
          type="default"
          className="pos-action-btn pos-action-btn--neutral"
          onClick={handleSuspend}
          size="large"
          loading={loading}
          disabled={cart.length === 0}
        >
          SUSPEND
        </Button>
        <Button
          block
          type="default"
          className="pos-action-btn pos-action-btn--confirm"
          icon={<CheckCircleOutlined />}
          onClick={() => handleCompleteOrder(false)}
          size="large"
          loading={loading}
          disabled={cart.length === 0 || loading}
        >
          Complete only
        </Button>
        <Button
          block
          type="primary"
          className="pos-action-btn pos-action-btn--complete-print"
          icon={<PrinterOutlined />}
          onClick={() => handleCompleteOrder(true)}
          size="large"
          loading={loading}
          disabled={cart.length === 0 || loading}
        >
          Complete &amp; Print
        </Button>
      </div>
      </div>
    </Card>
  );

  // Render Cart View
  const renderCartLineItems = () => (
    <div className="pos-cart-lines">
      {cart.map((item, index) => {
        const unitPrice = item.sale_unit_price ?? item.unit_price;
        const tint = getCartLineTint(index);
        const charged = item.quantity * (Number(unitPrice) || 0);
        const lineGst = gstEnabled
          ? computeLineGst(charged, item.gst_category, gstInclusive, Number(item.gst_rate ?? gstRate))
          : null;
        return (
          <Card
            key={item.id}
            size="small"
            className="pos-cart-line-card"
            style={posItemTintStyle(tint)}
          >
            <div className="pos-cart-line">
              <div className="pos-cart-line-main">
                <div className="pos-cart-line-info">
                  <Text className="pos-cart-line-name" strong>
                    {item.product_name}
                    {(Number(item.sale_unit_price ?? item.unit_price) || 0) < (Number(item.unit_price) || 0) ? (
                      <Tag color="orange" style={{ marginLeft: 8 }}>Discounted</Tag>
                    ) : null}
                  </Text>
                  <Text className="app-mono pos-cart-line-qty-meta">
                    Qty {item.quantity} {sellUnitLabel((item.sell_unit || 'base') as SellUnit).toLowerCase()}
                    {(item.units_per || 1) > 1 ? ` (${toBaseQuantity(item.quantity, item.units_per || 1)} pcs)` : ''}
                    {' '}× Nu. {(Number(unitPrice) || 0).toFixed(2)}
                    {(Number(item.sale_unit_price ?? item.unit_price) || 0) < (Number(item.unit_price) || 0)
                      ? ` (list Nu. ${(Number(item.unit_price) || 0).toFixed(2)})`
                      : ''}
                    {item.price_mode === 'wholesale' ? ' · Wholesale' : ''}
                  </Text>
                  {(item.packaging && availableSellUnits(item.packaging).length > 1) ? (
                    <Select
                      className="pos-unit-select"
                      dropdownClassName="pos-unit-select-dropdown"
                      value={item.sell_unit || 'base'}
                      options={availableSellUnits(item.packaging).map((u) => {
                        const pieces = unitsPerSellUnit(item.packaging!, u);
                        const name = sellUnitLabel(u);
                        return {
                          value: u,
                          label: u === 'base' || pieces <= 1 ? name : `${name} · ${pieces} pcs`,
                        };
                      })}
                      onChange={(v) => setCartSellUnit(item.id, v as SellUnit)}
                      aria-label="Sell unit"
                    />
                  ) : null}
                  {lineGst && (
                    <Text className="app-mono pos-cart-line-gst">
                      {gstCartLineCaption(lineGst, gstInclusive)}
                    </Text>
                  )}
                  <Text type="secondary" className="app-mono pos-cart-line-sku">
                    SKU {item.barcode || item.product_code}
                  </Text>
                  <Popover
                    trigger="click"
                    title="Sale price (per unit)"
                    content={
                      <div style={{ minWidth: 220 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          List: {(Number(item.unit_price) || 0).toFixed(2)} Nu./ea
                          {(Number(item.sale_unit_price ?? item.unit_price) || 0) < (Number(item.unit_price) || 0)
                            ? ' — set a lower clearance/discount price if needed'
                            : ''}
                        </Text>
                        <InputNumber
                          size="small"
                          min={0}
                          step={0.001}
                          value={item.sale_unit_price ?? item.unit_price}
                          onChange={(v) => setLineSaleUnitPrice(item.id, v)}
                          style={{ width: '100%' }}
                          className="app-mono"
                        />
                        <Button
                          type="link"
                          size="small"
                          style={{ paddingLeft: 0, marginTop: 4 }}
                          onClick={() => setLineSaleUnitPrice(item.id, item.unit_price)}
                        >
                          Reset to list price
                        </Button>
                      </div>
                    }
                  >
                    <Button type="link" size="small" className="pos-line-adjust-btn">
                      Adjust price
                    </Button>
                  </Popover>
                </div>
                <div className="pos-cart-line-amounts">
                  <Text className="app-mono pos-cart-line-unit">
                    {(Number(unitPrice) || 0).toFixed(2)} Nu.
                  </Text>
                  <Text strong className="app-mono pos-cart-line-total">
                    {(Number(item.total_price) || 0).toFixed(2)} Nu.
                  </Text>
                </div>
              </div>

              <div className="pos-cart-line-actions">
                <div className="pos-qty-controls">
                  <Button
                    className="pos-icon-button pos-icon-button--primary pos-icon-button--qty"
                    shape="round"
                    icon={<MinusOutlined />}
                    onClick={() => decreaseCartQuantity(item.id)}
                    aria-label="Decrease quantity"
                  />
                  <InputNumber
                    min={1}
                    max={
                      item.stock_quantity != null
                        ? Math.max(1, Math.floor(item.stock_quantity / Math.max(1, item.units_per || 1)))
                        : undefined
                    }
                    value={item.quantity}
                    onChange={(value) => {
                      if (value != null && value > 0) {
                        const delta = value - item.quantity;
                        updateCartQuantity(item.id, delta);
                      }
                    }}
                    className="pos-cart-qty-input app-mono"
                    controls={false}
                    bordered
                  />
                  <Button
                    className="pos-icon-button pos-icon-button--primary pos-icon-button--qty"
                    shape="round"
                    icon={<PlusOutlined />}
                    onClick={() => updateCartQuantity(item.id, 1)}
                    disabled={item.stock_quantity != null && item.quantity >= item.stock_quantity}
                    aria-label="Increase quantity"
                  />
                </div>
                <Button
                  className="pos-icon-button pos-icon-button--danger pos-icon-button--qty pos-icon-button--delete"
                  shape="round"
                  icon={<DeleteOutlined />}
                  onClick={() => removeFromCart(item.id)}
                  aria-label="Remove item"
                />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );

  // Render Cart View
  const renderCartView = (): JSX.Element => (
    <div
      className="pos-shell"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      <Row
        gutter={[12, 12]}
        className="pos-sale-layout-row pos-sale-layout-row--active"
        style={{ margin: 0, flex: 1, display: 'flex', minHeight: 0, width: '100%' }}
        wrap
      >
        <Col
          xs={24}
          md={cart.length === 0 ? 24 : 12}
          lg={cart.length === 0 ? 24 : 12}
          xl={cart.length === 0 ? 24 : 12}
          className="pos-sale-left"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
        >
          <Card
            className="pos-column-card pos-column-card--sale-left"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 16 }}
          >
            <div className="pos-sale-toolbar">
              {renderProductSearchToolbar()}
            </div>
            <div className="pos-product-results-compact">
              {renderProductResults()}
            </div>
          </Card>
        </Col>
        {cart.length > 0 ? (
        <Col xs={24} md={12} lg={12} xl={12} className="pos-sale-right pos-sale-right--pop" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
          <div
            className="pos-right-scroll app-show-scrollbar"
            ref={setCartScrollNode}
            tabIndex={0}
            onMouseEnter={() => cartScrollRef.current?.focus({ preventScroll: true })}
          >
            <Card
              className="pos-column-card pos-ticket-card"
              bodyStyle={{ padding: 12 }}
            >
              <div className="pos-cart-section pos-cart-section--ticket">
                <div className="pos-cart-section-head">
                  <PosIconBadge name="cart" tone="blue" size={18} />
                  <span>Current sale</span>
                  {gstEnabled ? (
                    <span className="pos-checkout-ord-badge pos-checkout-ord-badge--gst">TAX INVOICE</span>
                  ) : null}
                  <span className="pos-checkout-ord-badge">
                    {cartLineCount} line{cartLineCount === 1 ? '' : 's'} · {totalCartQty} qty
                  </span>
                </div>
                {renderCartLineItems()}
                <div className="pos-ticket-due">
                  {gstEnabled ? (
                    <>
                      {gstInclusive ? (
                        <div className="pos-ticket-due-row pos-ticket-due-row--gst">
                          <span>Taxable (ex-GST)</span>
                          <span className="app-mono">Nu. {gstTaxablePreview.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="pos-ticket-due-row pos-ticket-due-row--gst">
                          <span>Subtotal</span>
                          <span className="app-mono">Nu. {totalDue.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="pos-ticket-due-row pos-ticket-due-row--gst">
                        <span>GST {gstRate}%{gstInclusive ? ' (included)' : ''}</span>
                        <span className="app-mono">Nu. {gstPreview.toFixed(2)}</span>
                      </div>
                    </>
                  ) : null}
                  <div className="pos-ticket-due-row">
                    <span>Total due</span>
                    <strong className="app-mono">Nu. {(Number(grandTotal) || 0).toFixed(2)}</strong>
                  </div>
                  {lineDiscountTotal > 0 ? (
                    <div className="pos-ticket-due-note">Discount Nu. {lineDiscountTotal.toFixed(2)}</div>
                  ) : null}
                  {!chargeOpen ? (
                    <Button
                      type="primary"
                      block
                      size="large"
                      className="pos-charge-btn"
                      onClick={() => setChargeOpen(true)}
                    >
                      Charge Nu. {(Number(grandTotal) || 0).toFixed(2)}
                    </Button>
                  ) : (
                    <Button type="link" className="pos-charge-back" onClick={() => setChargeOpen(false)}>
                      Back to ticket
                    </Button>
                  )}
                </div>
              </div>
            </Card>
            <div className="pos-right-pay">
              {chargeOpen ? renderPaymentPanel() : null}
            </div>
          </div>
        </Col>
        ) : null}
      </Row>
    </div>
  );

  // Render Stashed View
  const renderStashedView = (): JSX.Element => (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <Row gutter={[16, 16]} style={{ margin: 0, flex: 1, display: 'flex', minHeight: 0, width: '100%' }}>
        <Col 
          xs={24} 
          sm={24} 
          md={selectedStashed ? 12 : 24} 
          lg={selectedStashed ? 10 : 24} 
          xl={selectedStashed ? 9 : 24} 
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
        <Card 
          title={<><SaveOutlined /> STASHED TRANSACTIONS</>}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          bodyStyle={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          {stashedList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <SaveOutlined style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
              <div style={{ fontSize: 14 }}>No stashed transactions</div>
              <div style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>Suspended transactions will appear here</div>
            </div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: 'calc(100dvh - 200px)', paddingRight: '8px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {stashedList.map(stashed => (
                  <Card 
                    key={stashed.id} 
                    hoverable
                    onClick={() => setSelectedStashed(stashed)}
                    style={{
                      cursor: 'pointer',
                      border: selectedStashed?.id === stashed.id ? '2px solid #1890ff' : '1px solid #e8e8e8',
                      transition: 'all 0.3s ease',
                      backgroundColor: selectedStashed?.id === stashed.id ? '#e6f7ff' : '#fff'
                    }}
                  >
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                      <Col xs={24} sm={16} md={16} lg={16}>
                        <Text strong style={{ fontSize: '14px', display: 'block' }}>
                          SERIAL: {stashed.serial}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          TIME: {stashed.time}
                        </Text>
                        <Text style={{ fontSize: '12px', display: 'block' }}>
                          ITEMS: {stashed.items.length}
                        </Text>
                        <Text strong style={{ fontSize: '14px', color: '#1890ff', display: 'block' }}>
                          AMOUNT: {(Number(stashed.total_amount) || 0).toFixed(2)} Nu.
                        </Text>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingLeft: '8px' }}>
                        <Button
                          type="primary"
                          icon={<ReloadOutlined style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetrieveStashed(stashed);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: '0 16px',
                            borderRadius: '6px',
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            lineHeight: '1'
                          }}
                        >
                          RETRIEVE
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            </div>
          )}
        </Card>
        </Col>

        {selectedStashed && (
        <Col xs={24} sm={24} md={12} lg={14} xl={15} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <Button
                  type="text"
                  onClick={() => setSelectedStashed(null)}
                  style={{
                    padding: 0,
                    marginRight: 8,
                    flexShrink: 0,
                    minWidth: 56,
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 40,
                    fontWeight: 500,
                    color: '#333',
                    lineHeight: 1,
                  }}
                  aria-label="Close stashed details"
                >
                  <span style={{ fontSize: 40, fontWeight: 500, display: 'inline-block', lineHeight: 1 }}>×</span>
                </Button>
                <SaveOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
                <span style={{ fontSize: '14px', fontWeight: 600 }}>Stashed Transaction Details</span>
              </div>
            }
            style={{ 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
              height: '100%',
              maxHeight: 'calc(100dvh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden'
            }}
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<ReloadOutlined style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center' }} />}
                  onClick={() => handleRetrieveStashed(selectedStashed)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '0 20px',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    lineHeight: '1'
                  }}
                >
                  Retrieve to Cart
                </Button>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined style={{ margin: 0 }} />}
                  onClick={async () => {
                    Modal.confirm({
                      title: 'Delete Stashed Transaction',
                      content: 'Are you sure you want to delete this stashed transaction? This action cannot be undone.',
                      okText: 'Delete',
                      okType: 'danger',
                      cancelText: 'Cancel',
                      onOk: async () => {
                        try {
                          if (selectedStashed.id.startsWith('offline-stash-')) {
                            await deleteOfflineStash(selectedStashed.id);
                          } else {
                            await deleteStashedTransaction(selectedStashed.id);
                          }
                          message.success('Stashed transaction deleted');
                          setSelectedStashed(null);
                          loadStashedTransactions();
                        } catch (error) {
                          message.error('Failed to delete stashed transaction');
                        }
                      },
                    });
                  }}
                >
                  Delete
                </Button>
              </Space>
            }
            bodyStyle={{ 
              flex: 1, 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              padding: '0',
              minHeight: 0,
              maxHeight: 'calc(100dvh - 280px)'
            }}
          >
            <div style={{ 
              flex: 1,
              overflowY: 'auto', 
              overflowX: 'hidden',
              padding: '16px',
              paddingRight: '12px',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              {/* Transaction Info Header */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '6px', flexShrink: 0 }}>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Serial Number:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>{selectedStashed.serial}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Stashed Time:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>{selectedStashed.time}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Total Items:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>{selectedStashed.items.length} items</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Total Amount:</Text>
                    <Text strong style={{ fontSize: '16px', color: '#1890ff', display: 'block' }}>
                      {(Number(selectedStashed.total_amount) || 0).toFixed(2)} Nu.
                    </Text>
                  </Col>
                </Row>
              </div>

              {/* Items List */}
              <div style={{ 
                flexShrink: 0,
                marginBottom: '16px'
              }}>
                <Text strong style={{ fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                  Items ({selectedStashed.items.length}):
                </Text>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {selectedStashed.items.map((item, idx) => (
                    <Card 
                      key={idx} 
                      size="small" 
                      style={{ 
                        backgroundColor: '#fafafa',
                        border: '1px solid #e8e8e8',
                        transition: 'all 0.2s ease'
                      }}
                      hoverable
                    >
                      <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col xs={24} sm={12} md={10} style={{ minWidth: 0 }}>
                          <Text strong style={{ fontSize: '14px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.product_name}
                          </Text>
                          <Text type="secondary" className="app-mono" style={{ fontSize: '11px', display: 'block' }}>
                            {item.barcode || item.product_code}
                          </Text>
                        </Col>
                        <Col xs={12} sm={6} md={4} style={{ textAlign: 'center' }}>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Unit Price</Text>
                          <Text className="app-mono" style={{ fontSize: '13px', fontWeight: 500 }}>
                            {(Number(item.sale_unit_price ?? item.unit_price) || 0).toFixed(2)} Nu.
                          </Text>
                          {(item.sale_unit_price ?? item.unit_price) < item.unit_price && (
                            <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                              List {(Number(item.unit_price) || 0).toFixed(2)}
                            </Text>
                          )}
                        </Col>
                        <Col xs={6} sm={4} md={3} style={{ textAlign: 'center' }}>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Qty</Text>
                          <Badge count={item.quantity} style={{ backgroundColor: '#1890ff' }} />
                        </Col>
                        <Col xs={6} sm={6} md={5} style={{ textAlign: 'right' }}>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Total</Text>
                          <Text strong className="app-mono" style={{ fontSize: '14px', color: '#52c41a' }}>
                            {(Number(item.total_price) || 0).toFixed(2)} Nu.
                          </Text>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              </div>

              {/* Footer Summary - Below all items */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#e6f7ff', 
                borderRadius: '6px',
                border: '1px solid #91d5ff',
                flexShrink: 0,
                marginTop: 'auto'
              }}>
                <Divider style={{ margin: '0 0 12px 0' }} />
                <Row justify="space-between" align="middle" gutter={[8, 0]}>
                  <Col flex="auto" style={{ padding: 0 }}>
                    <Text strong style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>Total Amount:</Text>
                  </Col>
                  <Col flex="none" style={{ padding: 0 }}>
                    <Text strong style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', color: '#1890ff' }}>
                      {(Number(selectedStashed.total_amount) || 0).toFixed(2)} Nu.
                    </Text>
                  </Col>
                </Row>
              </div>
            </div>
          </Card>
        </Col>
        )}
      </Row>
    </div>
  );

  // Render Transactions View
  const renderTransactionsView = (): JSX.Element => (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <Row gutter={[16, 16]} style={{ margin: 0, flex: 1, display: 'flex', minHeight: 0, width: '100%' }}>
        <Col 
          xs={24} 
          sm={24} 
          md={selectedTransaction ? 12 : 24} 
          lg={selectedTransaction ? 10 : 24} 
          xl={selectedTransaction ? 9 : 24} 
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
        <Card 
          title={
            <div className="pos-transaction-header" style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
              <span
                className={`pos-transaction-header-title ${transactionListFilter === 'all' ? 'pos-transaction-tab-active' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setTransactionListFilter('all')}
              >
                TRANSACTIONS
              </span>
              <span
                className={`pos-transaction-header-title pos-refund-btn ${transactionListFilter === 'refunded' ? 'pos-transaction-tab-active' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setTransactionListFilter('refunded')}
              >
                REFUND
              </span>
            </div>
          }
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          bodyStyle={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          {/* Filters (receipt search) */}
          <div style={{ padding: '12px 12px 0 12px' }}>
            <div className="pos-filterBar">
              <Row gutter={[10, 10]} align="middle">
                <Col xs={24} sm={24} md={10} lg={10} xl={10}>
                  <div className="pos-inlineSearch">
                    <Input
                      className="app-search"
                      prefix={<SearchOutlined />}
                      placeholder="Search receipt (Transaction No. or Serial)"
                      value={txnSearchInput}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTxnSearchInput(v);
                        if (!v) setTxnSearch('');
                      }}
                      onPressEnter={() => setTxnSearch((txnSearchInput || '').trim())}
                      allowClear
                    />
                    <Button
                      className="pos-txnSearchBtn"
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={() => setTxnSearch((txnSearchInput || '').trim())}
                      aria-label="Search receipt"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <DatePicker.RangePicker
                  className="pos-txnFilter"
                  style={{ width: '100%' }}
                  value={txnDateRange}
                  onChange={(v) => setTxnDateRange(v)}
                  allowClear
                />
                </Col>
                <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                <Select
                  value={txnPayment}
                  onChange={setTxnPayment}
                  className="pos-txnFilter"
                  style={{ width: '100%' }}
                  options={[
                    { value: 'ALL', label: 'All payment methods' },
                    { value: 'CASH', label: 'Cash' },
                    { value: 'CARD', label: 'Card' },
                    { value: 'BANK_TRANSFER', label: 'Bank transfer' },
                  ]}
                />
                </Col>
              </Row>
            </div>
            <Divider style={{ margin: '12px 0' }} />
          </div>

          {(() => {
            const filteredList = transactionListFilter === 'refunded'
              ? transactionList.filter(t => t.status === 'REFUNDED' || t.status === 'PARTIALLY_REFUNDED')
              : transactionList;
            return filteredList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {transactionListFilter === 'refunded' ? (
                <>
                  <UndoOutlined style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
                  <div style={{ fontSize: 14 }}>No refunded transactions</div>
                  <div style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>Refunded and partially refunded transactions will appear here</div>
                </>
              ) : (
                <>
                  <HistoryOutlined style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
                  <div style={{ fontSize: 14 }}>No transactions found</div>
                  <div style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>Completed transactions will appear here</div>
                </>
              )}
            </div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: 'calc(100dvh - 200px)', paddingRight: '8px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {filteredList.map(transaction => (
                  <Card
                    key={transaction.id}
                    hoverable
                    onClick={() => handleViewTransaction(transaction)}
                    style={{
                      cursor: 'pointer',
                      border: selectedTransaction?.id === transaction.id ? '2px solid #1890ff' : '1px solid #e8e8e8',
                      transition: 'all 0.3s ease',
                      backgroundColor: selectedTransaction?.id === transaction.id ? '#e6f7ff' : '#fff'
                    }}
                  >
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                      <Col xs={24} sm={16} md={16} lg={16}>
                        <Text strong style={{ fontSize: '14px', display: 'block' }}>
                          {transaction.transaction_number} {transaction.serial ? `• ${transaction.serial}` : ''}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          {moment(transaction.sale_date).format('MM/DD/YYYY')} {transaction.sale_time}
                        </Text>
                        <Text style={{ fontSize: '12px', display: 'block' }}>
                          Qty: {transaction.items.reduce((sum, i) => sum + (i.quantity || 0), 0)} • Items: {transaction.items.length} • Cashier: {transaction.cashier_name || 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 2 }}>
                          Store: {formatPOSTransactionLocation(transaction)}
                        </Text>
                      </Col>
                      <Col xs={24} sm={8} md={8} lg={8} style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: '16px', color: '#52c41a', display: 'block' }}>
                          {(Number(transaction.total_amount) || 0).toFixed(2)} Nu.
                        </Text>
                        {(Number(transaction.tax_amount) || 0) > 0 && (
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 2 }} className="app-mono">
                            incl. tax {(Number(transaction.tax_amount) || 0).toFixed(2)} Nu.
                          </Text>
                        )}
                        <ReceiptPaymentMethods transaction={transaction} compact />
                        {transaction.status === 'REFUNDED' && (
                          <Tag color="orange" style={{ marginTop: '4px', marginLeft: '4px', fontSize: '12px' }}>
                            Refunded
                          </Tag>
                        )}
                        {transaction.status === 'PARTIALLY_REFUNDED' && (
                          <Tag color="gold" style={{ marginTop: '4px', marginLeft: '4px', fontSize: '12px' }}>
                            Partially refunded
                          </Tag>
                        )}
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            </div>
          );
          })()}
        </Card>
        </Col>

        {selectedTransaction && (
        <Col xs={24} sm={24} md={12} lg={14} xl={15} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <Button
                  type="text"
                  onClick={() => setSelectedTransaction(null)}
                  style={{
                    padding: 0,
                    marginRight: 8,
                    flexShrink: 0,
                    minWidth: 56,
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 40,
                    fontWeight: 500,
                    color: '#333',
                    lineHeight: 1,
                  }}
                  aria-label="Close transaction details"
                >
                  <span style={{ fontSize: 40, fontWeight: 500, display: 'inline-block', lineHeight: 1 }}>×</span>
                </Button>
                <HistoryOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
                <span style={{ fontSize: '14px', fontWeight: 600 }}>Transaction Details</span>
              </div>
            }
              extra={
              <Space>
                {selectedTransaction?.status !== 'REFUNDED' && (
                  <Button
                    type="primary"
                    danger
                    icon={<ReloadOutlined style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center' }} />}
                    onClick={() => {
                      setRefundItems([]);
                      setShowRefundModal(true);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      height: '40px',
                      fontSize: '14px',
                      fontWeight: 600,
                      padding: '0 20px',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap',
                      lineHeight: '1'
                    }}
                  >
                    REFUND
                  </Button>
                )}
                <Button 
                  type="default" 
                  icon={<PrinterOutlined style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center' }} />}
                  onClick={async () => {
                    if (!selectedTransaction) return;
                    const full = await getTransactionById(selectedTransaction.id);
                    const txn = full ?? selectedTransaction;
                    const printed = printPosReceipt(
                      txn,
                      formatPOSTransactionLocation(txn),
                    );
                    if (!printed) {
                      message.warning('Could not open receipt preview.');
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '0 20px',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    lineHeight: '1'
                  }}
                >
                  PRINT
                </Button>
              </Space>
            }
            style={{ 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
              height: '100%',
              maxHeight: 'calc(100dvh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden'
            }}
            bodyStyle={{ 
              flex: 1, 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              padding: '0',
              minHeight: 0,
              maxHeight: 'calc(100dvh - 280px)'
            }}
          >
            <div style={{ 
              flex: 1,
              overflowY: 'auto', 
              overflowX: 'hidden',
              padding: '16px',
              paddingRight: '12px',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              {/* Transaction Info Header */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '6px', flexShrink: 0 }}>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Transaction ID:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>{selectedTransaction.transaction_number}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Date & Time:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>
                      {moment(selectedTransaction.sale_date).format('YYYY-MM-DD')} {selectedTransaction.sale_time}
                    </Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Total Items:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>{selectedTransaction.items.length} items</Text>
                  </Col>
                  <Col xs={24}>
                    <ReceiptPaymentMethods transaction={selectedTransaction} />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Receipt:</Text>
                    <Tag color={selectedTransaction.receipt_printed === false ? 'default' : 'blue'} style={{ marginTop: 4 }}>
                      {selectedTransaction.receipt_printed === false ? 'No receipt given' : 'Printed / given'}
                    </Tag>
                  </Col>
                  {selectedTransaction.cashier_name && (
                    <Col xs={24} sm={12}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Cashier:</Text>
                      <Text strong style={{ fontSize: '14px', display: 'block' }}>{selectedTransaction.cashier_name}</Text>
                    </Col>
                  )}
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Store / location:</Text>
                    <Text strong style={{ fontSize: '14px', display: 'block' }}>
                      {formatPOSTransactionLocation(selectedTransaction)}
                    </Text>
                  </Col>
                </Row>
              </div>

              {/* Items List */}
              <div style={{ 
                flexShrink: 0,
                marginBottom: '16px'
              }}>
                <Text strong style={{ fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                  Items ({selectedTransaction.items.length}):
                </Text>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {selectedTransaction.items.map((item, idx) => (
                    <Card 
                      key={idx} 
                      size="small" 
                      style={{ 
                        backgroundColor: '#fafafa',
                        border: '1px solid #e8e8e8',
                        transition: 'all 0.2s ease'
                      }}
                      hoverable
                    >
                      <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col xs={24} sm={12} md={10} style={{ minWidth: 0 }}>
                          <Text strong style={{ fontSize: '14px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.product_name}
                          </Text>
                          <Text type="secondary" className="app-mono" style={{ fontSize: '11px', display: 'block' }}>
                            {item.barcode || item.product_code}
                          </Text>
                        </Col>
                        <Col xs={12} sm={6} md={4} style={{ textAlign: 'center' }}>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Unit Price</Text>
                          <Text className="app-mono" style={{ fontSize: '13px', fontWeight: 500 }}>
                            {(Number(item.sale_unit_price ?? item.unit_price) || 0).toFixed(2)} Nu.
                          </Text>
                          {(item.sale_unit_price ?? item.unit_price) < item.unit_price && (
                            <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                              List {(Number(item.unit_price) || 0).toFixed(2)}
                            </Text>
                          )}
                        </Col>
                        <Col xs={6} sm={4} md={3} style={{ textAlign: 'center' }}>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Qty</Text>
                          <Badge count={item.quantity} style={{ backgroundColor: '#1890ff' }} />
                        </Col>
                        <Col xs={6} sm={6} md={5} style={{ textAlign: 'right' }}>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Total</Text>
                          <Text strong className="app-mono" style={{ fontSize: '14px', color: '#52c41a' }}>
                            {(Number(item.total_price) || 0).toFixed(2)} Nu.
                          </Text>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              </div>

              {/* Footer Summary — line subtotal, tax, discounts, then grand total */}
              {(() => {
                const tx = selectedTransaction;
                const itemsSubtotal = tx.items.reduce(
                  (s, i) => s + (Number(i.total_price) || 0),
                  0,
                );
                const tax = Math.max(0, Number(tx.tax_amount) || 0);
                const disc = Math.max(0, Number(tx.discount_amount) || 0);
                const coupon = Math.max(0, Number(tx.coupon_discount) || 0);
                const total = Number(tx.total_amount) || 0;
                const taxRateLabel =
                  itemsSubtotal > 0 && tax > 0
                    ? ` (${((tax / itemsSubtotal) * 100).toFixed(2)}% of subtotal)`
                    : '';
                const summaryRow = (
                  rowKey: string,
                  label: React.ReactNode,
                  value: string,
                  valueColor?: string,
                  strongValue?: boolean,
                ) => (
                  <Row
                    key={rowKey}
                    justify="space-between"
                    align="middle"
                    gutter={[8, 4]}
                    style={{ marginBottom: 6 }}
                  >
                    <Col flex="auto" style={{ padding: 0 }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {label}
                      </Text>
                    </Col>
                    <Col flex="none" style={{ padding: 0 }}>
                      <Text
                        strong={strongValue}
                        className="app-mono"
                        style={{ fontSize: 13, color: valueColor || '#262626' }}
                      >
                        {value} Nu.
                      </Text>
                    </Col>
                  </Row>
                );
                return (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#e6f7ff',
                      borderRadius: '6px',
                      border: '1px solid #91d5ff',
                      flexShrink: 0,
                      marginTop: 'auto',
                    }}
                  >
                    <Divider style={{ margin: '0 0 12px 0' }} />
                    {summaryRow('subtotal', 'Subtotal (items)', (Number(itemsSubtotal) || 0).toFixed(2))}
                    {disc > 0
                      ? summaryRow('discount', 'Discount', `− ${disc.toFixed(2)}`, '#16a34a')
                      : null}
                    {coupon > 0
                      ? summaryRow('coupon', 'Coupon', `− ${coupon.toFixed(2)}`, '#16a34a')
                      : null}
                    {tx.is_tax_invoice && tax > 0.0005
                      ? summaryRow(
                          'tax',
                          <span>GST {gstRate}% (included in prices)</span>,
                          tax.toFixed(2),
                          '#0f766e',
                        )
                      : !tx.is_tax_invoice && tax > 0
                      ? summaryRow(
                          'tax',
                          <span>
                            Tax
                            <Text type="secondary" style={{ fontWeight: 400 }}>
                              {taxRateLabel}
                            </Text>
                          </span>,
                          tax.toFixed(2),
                          '#d97706',
                        )
                      : null}
                    {tax <= 0 &&
                    disc <= 0 &&
                    coupon <= 0 &&
                    Math.abs(total - itemsSubtotal) > 0.0005 ? (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        Other charges / rounding: {(total - itemsSubtotal).toFixed(2)} Nu.
                      </Text>
                    ) : null}
                    <Divider style={{ margin: '8px 0 10px 0' }} />
                    <Row justify="space-between" align="middle" gutter={[8, 0]}>
                      <Col flex="auto" style={{ padding: 0 }}>
                        <Text strong style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>
                          Total amount:
                        </Text>
                      </Col>
                      <Col flex="none" style={{ padding: 0 }}>
                        <Text
                          strong
                          style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', color: '#1890ff' }}
                          className="app-mono"
                        >
                          {total.toFixed(2)} Nu.
                        </Text>
                      </Col>
                    </Row>
                  </div>
                );
              })()}
            </div>
          </Card>
        </Col>
        )}
      </Row>
    </div>
  );

  return (
    <div className="pos-shell">
      {/* Till, Transactions, and Stash stay visible at the top of the POS. */}
      <div className="pos-topbar">
        <div className="pos-view-switch" role="tablist" aria-label="Till screens">
          <Button
            type={currentView === 'cart' ? 'primary' : 'default'}
            className="pos-view-switch-btn"
            onClick={() => setCurrentView('cart')}
          >
            Till
          </Button>
          <Button
            type={currentView === 'transactions' ? 'primary' : 'default'}
            className="pos-view-switch-btn"
            onClick={() => setCurrentView('transactions')}
          >
            Transactions{transactionList.length > 0 ? ` (${transactionList.length})` : ''}
          </Button>
          <Button
            type={currentView === 'stashed' ? 'primary' : 'default'}
            className="pos-view-switch-btn"
            onClick={() => setCurrentView('stashed')}
          >
            Stash{stashedList.length > 0 ? ` (${stashedList.length})` : ''}
          </Button>
        </div>
      </div>

      <div className="pos-content">
        {currentView === 'cart' && renderCartView()}
        {currentView === 'stashed' && renderStashedView()}
        {currentView === 'transactions' && renderTransactionsView()}
      </div>

      <CreditBookModal open={creditBookOpen} onClose={() => setCreditBookOpen(false)} />

      {/* Barcode / SKU — visible modal avoids focus on hidden input breaking clicks & hover */}
      <Modal
        title="Add by barcode"
        open={barcodeScanModalOpen}
        onCancel={() => {
          setBarcodeScanModalOpen(false);
          setBarcodeInput('');
        }}
        onOk={async () => {
          const ok = await handleBarcodeSubmit();
          if (!ok) return Promise.reject();
          setBarcodeScanModalOpen(false);
        }}
        okText="Add to cart"
        cancelText="Cancel"
        destroyOnClose
        width={420}
        maskClosable
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Use a USB scanner (it will type here) or enter the barcode / SKU, then press Enter or Add to cart.
        </Text>
        <Input
          size="large"
          placeholder="Scan or type barcode"
          value={barcodeInput}
          onChange={(e) => setBarcodeInput(e.target.value)}
          onPressEnter={async () => {
            if (await handleBarcodeSubmit()) setBarcodeScanModalOpen(false);
          }}
          autoFocus
          allowClear
          autoComplete="off"
        />
      </Modal>

      {/* Payment Modal — mobile transfer shows amount QR + journal number + phone */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600 }}>
              {paymentMethod === POSPaymentMethod.MOBILE_TRANSFER && !splitPayment
                ? 'Mobile / bank transfer'
                : 'Payment Confirmation'}
            </span>
          </div>
        }
        open={showPaymentModal}
        onOk={async () => {
          if (splitPayment) {
            const splitNeedsPhone = splitLines.some(
              (line) => line.method === POSPaymentMethod.MOBILE_TRANSFER,
            );
            if (splitNeedsPhone) {
              const phoneErr = validatePayerPhone(paymentPayerPhone);
              if (phoneErr) {
                message.warning(phoneErr);
                return;
              }
            }
            const splitError = validateSplitPaymentLines(splitLines, grandTotal);
            if (splitError) {
              message.warning(splitError);
              return;
            }
            await processPayment('', splitNeedsPhone ? paymentPayerPhone : '');
            return;
          }
          if (paymentMethod === POSPaymentMethod.MOBILE_TRANSFER) {
            const mobileErr = validateMobilePayFields(paymentReference, paymentPayerPhone);
            if (mobileErr) {
              message.warning(mobileErr);
              return;
            }
          }
          // Cash: no phone. Card: phone optional.
          await processPayment(
            paymentReference,
            paymentMethod === POSPaymentMethod.CASH ? '' : paymentPayerPhone,
          );
        }}
        onCancel={() => {
          setShowPaymentModal(false);
          setPaymentReference('');
          setPaymentPayerPhone('');
        }}
        okText="Confirm Payment"
        cancelText="Cancel"
        okButtonProps={{
          loading: loading,
          // Keep clickable so validation can show a toast; only block while saving.
          disabled: loading,
        }}
        width={paymentMethod === POSPaymentMethod.MOBILE_TRANSFER && !splitPayment ? 480 : 500}
        destroyOnClose
      >
        {!splitPayment && paymentMethod === POSPaymentMethod.MOBILE_TRANSFER ? (
          <MobileTransferPayPanel
            amountNu={Number(grandTotal) || 0}
            account={{
              legalName: taxSettings?.legal_name,
              accountName: taxSettings?.payment_account_name,
              accountNumber: taxSettings?.payment_account_number,
              bankName: taxSettings?.payment_account_bank,
            }}
            journalNumber={paymentReference}
            onJournalNumberChange={setPaymentReference}
            payerPhone={paymentPayerPhone}
            onPayerPhoneChange={setPaymentPayerPhone}
            onConfirm={async () => {
              const mobileErr = validateMobilePayFields(paymentReference, paymentPayerPhone);
              if (mobileErr) {
                message.warning(mobileErr);
                return;
              }
              await processPayment(paymentReference, paymentPayerPhone);
            }}
          />
        ) : (
        <Form layout="vertical" autoComplete="off">
          <Form.Item label="Payment Method">
            {splitPayment ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {splitLines.map((line) => (
                  <div
                    key={line.id}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Text strong>{paymentMethodLabel(line.method)}</Text>
                    <div className="app-mono" style={{ marginTop: 4 }}>
                      Nu. {(parseFloat(line.amount) || 0).toFixed(2)}
                      {line.reference ? ` · Jrn: ${line.reference}` : ''}
                      {paymentPayerPhone ? ` · Tel: ${paymentPayerPhone}` : line.phone ? ` · Tel: ${line.phone}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div style={{
              padding: '12px',
              backgroundColor: '#f0f2f5',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Text strong style={{ fontSize: '14px' }}>
                {paymentMethod === POSPaymentMethod.CASH ? 'Cash' :
                 paymentMethod === POSPaymentMethod.CARD_PAYMENT ? 'Card Payment' :
                 'Mobile Transfer'}
              </Text>
            </div>
            )}
          </Form.Item>
          <Form.Item label="Total Amount">
            <div style={{
              padding: '12px',
              backgroundColor: '#e6f7ff',
              borderRadius: '6px',
              border: '1px solid #91d5ff'
            }}>
              <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                {(Number(grandTotal) || 0).toFixed(2)} Nu.
              </Text>
            </div>
          </Form.Item>
          {!splitPayment && paymentMethod !== POSPaymentMethod.MOBILE_TRANSFER && (
            <Form.Item>
              <div style={{
                padding: '12px',
                backgroundColor: '#f6ffed',
                borderRadius: '6px',
                border: '1px solid #b7eb8f'
              }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  No reference number required for {paymentMethod === POSPaymentMethod.CASH ? 'cash' : 'card'} payments
                </Text>
              </div>
            </Form.Item>
          )}
        </Form>
        )}
      </Modal>

      {/* Refund Modal - Partial refund supported; refunded items shown inside */}
      <Modal
        title="Refund Transaction (Partial or Full)"
        open={showRefundModal}
        onOk={handleRefund}
        onCancel={() => {
          setShowRefundModal(false);
          setRefundItems([]);
        }}
        width={640}
        confirmLoading={loading}
        okText="Process Refund"
        cancelText="Cancel"
        okButtonProps={{ disabled: refundItems.length === 0 }}
      >
        {selectedTransaction && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Refund history - previous refunds for this transaction */}
            <div style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8, padding: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Refund history
              </Text>
              {refundHistoryLoading ? (
                <Text type="secondary">Loading...</Text>
              ) : refundHistory.length === 0 ? (
                <Text type="secondary">No refunds yet for this transaction.</Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {refundHistory.map((r) => (
                    <div key={r.id} style={{ fontSize: '12px', borderBottom: '1px solid #d9d9d9', paddingBottom: 6, marginBottom: 6 }}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text strong>{r.transaction_number}</Text>
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            {moment(r.created_at).format('YYYY-MM-DD HH:mm')} · {r.item_count} item(s)
                          </Text>
                        </Col>
                        <Col>
                          <Text strong style={{ color: '#ff4d4f' }}>{Math.abs(Number(r.total_amount) || 0).toFixed(2)} Nu.</Text>
                        </Col>
                      </Row>
                      {r.items_summary?.length > 0 && (
                        <div style={{ marginTop: 4, paddingLeft: 8, color: '#595959' }}>
                          {r.items_summary.map((s, i) => (
                            <div key={i}>{s.product_name} × {s.quantity} = {(Number(s.total_price) || 0).toFixed(2)} Nu.</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </Space>
              )}
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Sale location: <Text strong>{formatPOSTransactionLocation(selectedTransaction)}</Text>
            </Text>
            <Text type="secondary" style={{ display: 'block' }}>
              Set quantity to refund per item (0 = no refund). Partial refund is supported — e.g. bought 4, return 2.
            </Text>
            <Divider style={{ margin: '8px 0' }} />
            <Text strong>1. Select quantity to refund per item</Text>
            <div style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {selectedTransaction.items.map((item, idx) => {
                  const refundItem = refundItems.find(ri => ri.product_id === item.product_id);
                  const qtyToRefund = refundItem?.quantity || 0;
                  const alreadyReturned = refundHistory.reduce((sum, row) => {
                    const line = (row.items_summary || []).find(
                      (entry) =>
                        (entry.product_id != null && entry.product_id === item.product_id) ||
                        entry.product_name === item.product_name,
                    );
                    return sum + (Number(line?.quantity) || 0);
                  }, 0);
                  const remaining = Math.max(0, item.quantity - alreadyReturned);
                  return (
                    <Card key={idx} size="small" style={{ backgroundColor: qtyToRefund > 0 ? '#fff7e6' : '#fafafa' }}>
                      <Row align="middle" gutter={16}>
                        <Col flex="auto">
                          <Text strong style={{ fontSize: '13px', display: 'block' }}>{item.product_name}</Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Sold: {item.quantity} × {(Number(item.sale_unit_price ?? item.unit_price) || 0).toFixed(2)} Nu.
                            {alreadyReturned > 0 ? ` · ${alreadyReturned} already returned · ${remaining} left` : ''}
                          </Text>
                        </Col>
                        <Col>
                          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 4 }}>
                            Qty to refund (max {remaining})
                          </Text>
                          <InputNumber
                            min={0}
                            max={remaining}
                            disabled={remaining <= 0}
                            value={Math.min(qtyToRefund, remaining)}
                            onChange={(value) => {
                              const newRefundItems = refundItems.filter(ri => ri.product_id !== item.product_id);
                              const next = Math.min(remaining, Math.max(0, Number(value) || 0));
                              if (next > 0) {
                                newRefundItems.push({ product_id: item.product_id, quantity: next });
                              }
                              setRefundItems(newRefundItems);
                            }}
                            style={{ width: '100px' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Space>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <Text strong>2. Refund summary — items to refund</Text>
            <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12, minHeight: 60 }}>
              {refundItems.length === 0 ? (
                <Text type="secondary">No items selected. Set quantity above to add items to this refund.</Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {refundItems.map(ri => {
                    const item = selectedTransaction.items.find(i => i.product_id === ri.product_id);
                    const unitCharged = item ? Number(item.sale_unit_price ?? item.unit_price) : 0;
                    const lineTotal = item ? unitCharged * ri.quantity : 0;
                    return (
                      <Row key={ri.product_id} justify="space-between" align="middle">
                        <Col>
                          <Text strong>{item?.product_name}</Text>
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            {ri.quantity} × {(Number(unitCharged) || 0).toFixed(2)} Nu.
                          </Text>
                        </Col>
                        <Col>
                          <Text strong style={{ color: '#ff4d4f' }}>{(Number(lineTotal) || 0).toFixed(2)} Nu.</Text>
                        </Col>
                      </Row>
                    );
                  })}
                  <Divider style={{ margin: '8px 0' }} />
                  <Row justify="space-between" align="middle">
                    <Col><Text strong>Total refund amount</Text></Col>
                    <Col>
                      <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                        {(Number(refundItems.reduce((sum, ri) => {
                          const item = selectedTransaction.items.find(i => i.product_id === ri.product_id);
                          const u = item ? Number(item.sale_unit_price ?? item.unit_price) : 0;
                          return sum + (item ? u * ri.quantity : 0);
                        }, 0)) || 0).toFixed(2)} Nu.
                      </Text>
                    </Col>
                  </Row>
                </Space>
              )}
            </div>
          </Space>
        )}
      </Modal>

      <ReceiptPrintModal
        open={receiptPrintOpen}
        transaction={receiptPrintTxn}
        storeName={receiptPrintStore}
        onClose={() => {
          setReceiptPrintOpen(false);
          setReceiptPrintTxn(null);
        }}
      />
    </div>
  );
};

export default POSInterface;
